"use client";

/**
 * CanvasEditor.tsx
 *
 * Konva canvas renderer for the editor:
 * - Renders a single "page" artboard (A4 etc.) using mm->px conversion
 * - Renders elements: text, rect, image (image is placeholder unless element.image_url exists)
 * - Supports select on click + drag to move (updates x_mm/y_mm)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Konva from "konva";
import { Stage, Layer, Rect, Text, Group, Circle, Ellipse, Line, Path, Image as KonvaImage, Transformer } from "react-konva";
import { useEditorStore } from "./editorStore";
import { getMarginBoxesMm, normalizePageMarginsMm } from "./contentUnits";
import { SHAPE_PRESETS } from "./shapePresets";
import { getBackgroundPatternById, getBackgroundPatternUrl } from "../backgrounds/patternLibrary";
import { resolveCoverSpreadGeometry } from "@/lib/coverGeometry";
import ImageCropModal, { buildImageCropElementPatch } from "./ImageCropModal";

const PX_PER_MM = 96 / 25.4; // screen mapping (not print). 96dpi -> px per mm.

function mmToPx(mm: number) {
  return mm * PX_PER_MM;
}
function pxToMm(px: number) {
  return px / PX_PER_MM;
}
function ptToPx(pt: number) {
  // 1pt = 1/72 inch; at 96 dpi => 96/72 = 1.333...
  return pt * (96 / 72);
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

const TOOLBAR_WRAP =
  "flex items-center gap-2 rounded-lg border border-black/10 bg-white px-2 py-1 shadow-lg text-[#272727]";
const TOOLBAR_BTN =
  "rounded-md px-2 py-1 text-xs text-[#272727] hover:bg-gray-50 transition-colors";
const TOOLBAR_BTN_ICON =
  "rounded-md px-2 py-1 text-xs inline-flex items-center gap-1 text-[#272727] hover:bg-gray-50 transition-colors";
const TOOLBAR_BTN_ACTIVE =
  "bg-[#272727] text-white hover:bg-black";
const IMAGE_ZOOM_FACTOR = 1.1;
const IMG_ICONS = {
  crop: "✂️",
  done: "✅",
  cancel: "✖️",
  remove: "🗑️"
};

function makeThumbnailPng({
  layer,
  artWpx,
  artHpx,
  pixelRatio
}: {
  layer: Konva.Layer;
  artWpx: number;
  artHpx: number;
  pixelRatio: number;
}) {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "-10000px";
  host.style.width = "1px";
  host.style.height = "1px";
  document.body.appendChild(host);

  try {
    const stage2 = new Konva.Stage({
      container: host,
      width: artWpx,
      height: artHpx
    });

    const layer2 = layer.clone({ listening: false }) as Konva.Layer;
    layer2.x(0);
    layer2.y(0);

    const paper2 = layer2.findOne(".paper-rect") as Konva.Rect | null;
    if (paper2) {
      paper2.shadowBlur(0);
      paper2.shadowOpacity(0);
      paper2.shadowOffsetX(0);
      paper2.shadowOffsetY(0);
    }

    const transformerNodes = layer2.find("Transformer");
    transformerNodes.forEach((node) => node.destroy());

    stage2.add(layer2);
    stage2.draw();

    const url = stage2.toDataURL({
      x: 0,
      y: 0,
      width: artWpx,
      height: artHpx,
      pixelRatio,
      mimeType: "image/png"
    });

    stage2.destroy();
    return url;
  } finally {
    host.remove();
  }
}

function uniqueMm(values: number[]) {
  const seen = new Set<string>();
  const out: number[] = [];

  for (const value of values) {
    const key = value.toFixed(4);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }

  return out;
}

/**
 * Build guide targets in mm for snapping against trim, bleed, safe and center lines.
 */
function buildSnapTargetsMm({
  artWmm,
  artHmm,
  bleedMm,
  safeMm,
  showGutter,
  gutterXmm
}: {
  artWmm: number;
  artHmm: number;
  bleedMm: number;
  safeMm: number;
  showGutter: boolean;
  gutterXmm: number;
}) {
  const xs = uniqueMm([
    -bleedMm,
    0,
    safeMm,
    artWmm / 2,
    Math.max(0, artWmm - safeMm),
    artWmm,
    artWmm + bleedMm,
    ...(showGutter && gutterXmm > 0 ? [gutterXmm] : [])
  ]);

  const ys = uniqueMm([
    -bleedMm,
    0,
    safeMm,
    artHmm / 2,
    Math.max(0, artHmm - safeMm),
    artHmm,
    artHmm + bleedMm
  ]);

  return { xs, ys };
}

/**
 * Snap an element position and return matched guide targets for temporary guide rendering.
 */
function snapElementPositionMmWithGuide({
  xMm,
  yMm,
  wMm,
  hMm,
  xs,
  ys,
  thresholdMm
}: {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  xs: number[];
  ys: number[];
  thresholdMm: number;
}) {
  const anchorsX = [
    { value: xMm, toX: (v: number) => v },
    { value: xMm + wMm / 2, toX: (v: number) => v - wMm / 2 },
    { value: xMm + wMm, toX: (v: number) => v - wMm }
  ];

  const anchorsY = [
    { value: yMm, toY: (v: number) => v },
    { value: yMm + hMm / 2, toY: (v: number) => v - hMm / 2 },
    { value: yMm + hMm, toY: (v: number) => v - hMm }
  ];

  let bestX = xMm;
  let bestDx = thresholdMm + 1;
  let snappedXTarget: number | null = null;

  for (const anchor of anchorsX) {
    for (const target of xs) {
      const distance = Math.abs(anchor.value - target);
      if (distance <= thresholdMm && distance < bestDx) {
        bestDx = distance;
        bestX = anchor.toX(target);
        snappedXTarget = target;
      }
    }
  }

  let bestY = yMm;
  let bestDy = thresholdMm + 1;
  let snappedYTarget: number | null = null;

  for (const anchor of anchorsY) {
    for (const target of ys) {
      const distance = Math.abs(anchor.value - target);
      if (distance <= thresholdMm && distance < bestDy) {
        bestDy = distance;
        bestY = anchor.toY(target);
        snappedYTarget = target;
      }
    }
  }

  return { xMm: bestX, yMm: bestY, xTargetMm: snappedXTarget, yTargetMm: snappedYTarget };
}

// Clamp a box (element) so it stays fully inside a constraint box.
function clampToBoxMm({
  xMm,
  yMm,
  wMm,
  hMm,
  box
}: {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  box: { x: number; y: number; w: number; h: number };
}) {
  const maxX = box.x + Math.max(0, box.w - wMm);
  const maxY = box.y + Math.max(0, box.h - hMm);

  return {
    xMm: clamp(xMm, box.x, maxX),
    yMm: clamp(yMm, box.y, maxY)
  };
}

// Clamp a rectangle (x,y,w,h) so it fits fully inside a constraint box.
function clampRectIntoBoxMm(
  rect: { x: number; y: number; w: number; h: number },
  box: { x: number; y: number; w: number; h: number }
) {
  let w = rect.w;
  let h = rect.h;

  if (w > box.w) w = box.w;
  if (h > box.h) h = box.h;

  const maxX = box.x + Math.max(0, box.w - w);
  const maxY = box.y + Math.max(0, box.h - h);

  const x = Math.max(box.x, Math.min(maxX, rect.x));
  const y = Math.max(box.y, Math.min(maxY, rect.y));

  return { x, y, w, h };
}

// Pick left or right margin box on spreads based on element center x.
function pickMarginBoxMm(
  marginBoxes: Array<{ x: number; y: number; w: number; h: number }>,
  pageWmm: number,
  elXmm: number,
  elWmm: number
) {
  if (!Array.isArray(marginBoxes) || marginBoxes.length === 0) return null;
  if (marginBoxes.length === 1) return marginBoxes[0];

  const centerX = elXmm + elWmm / 2;
  return centerX < pageWmm ? marginBoxes[0] : marginBoxes[1];
}

/**
 * Snap a resize box (x,y,w,h) to guide lines using left/center/right and top/middle/bottom anchors.
 * Unlike drag snapping, resize snapping may adjust width/height (snapping right/bottom edges).
 */
function snapResizeBoxMmWithGuide({
  xMm,
  yMm,
  wMm,
  hMm,
  xs,
  ys,
  thresholdMm,
  minWmm = 5,
  minHmm = 5
}: {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  xs: number[];
  ys: number[];
  thresholdMm: number;
  minWmm?: number;
  minHmm?: number;
}) {
  let bestX = xMm;
  let bestW = wMm;
  let bestDx = thresholdMm + 1;
  let xGuide: number | null = null;

  for (const target of xs) {
    {
      const distance = Math.abs(xMm - target);
      const newW = xMm + wMm - target;
      if (distance <= thresholdMm && distance < bestDx && newW >= minWmm) {
        bestDx = distance;
        bestX = target;
        bestW = newW;
        xGuide = target;
      }
    }

    {
      const right = xMm + wMm;
      const distance = Math.abs(right - target);
      const newW = target - xMm;
      if (distance <= thresholdMm && distance < bestDx && newW >= minWmm) {
        bestDx = distance;
        bestX = xMm;
        bestW = newW;
        xGuide = target;
      }
    }

    {
      const center = xMm + wMm / 2;
      const distance = Math.abs(center - target);
      if (distance <= thresholdMm && distance < bestDx) {
        bestDx = distance;
        bestX = target - wMm / 2;
        bestW = wMm;
        xGuide = target;
      }
    }
  }

  let bestY = yMm;
  let bestH = hMm;
  let bestDy = thresholdMm + 1;
  let yGuide: number | null = null;

  for (const target of ys) {
    {
      const distance = Math.abs(yMm - target);
      const newH = yMm + hMm - target;
      if (distance <= thresholdMm && distance < bestDy && newH >= minHmm) {
        bestDy = distance;
        bestY = target;
        bestH = newH;
        yGuide = target;
      }
    }

    {
      const bottom = yMm + hMm;
      const distance = Math.abs(bottom - target);
      const newH = target - yMm;
      if (distance <= thresholdMm && distance < bestDy && newH >= minHmm) {
        bestDy = distance;
        bestY = yMm;
        bestH = newH;
        yGuide = target;
      }
    }

    {
      const middle = yMm + hMm / 2;
      const distance = Math.abs(middle - target);
      if (distance <= thresholdMm && distance < bestDy) {
        bestDy = distance;
        bestY = target - hMm / 2;
        bestH = hMm;
        yGuide = target;
      }
    }
  }

  return { xMm: bestX, yMm: bestY, wMm: bestW, hMm: bestH, xGuideMm: xGuide, yGuideMm: yGuide };
}

/**
 * Map built-in frame_id (clip frames) to a clip definition.
 * SVG frames are not clipped in Konva preview yet (server renderer supports them).
 */
function frameToClip(frameId?: string) {
  if (!frameId) return null;

  if (frameId === "frame_circle") return { type: "circle" as const };

  if (frameId.startsWith("frame_round_")) {
    const mm = Number(frameId.split("_").pop() || 0);
    return { type: "rounded" as const, radius_mm: mm };
  }

  const polygonMap: Record<string, Array<{ x: number; y: number }>> = {
    frame_triangle: [{ x: 50, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 }],
    frame_hexagon: [
      { x: 25, y: 0 }, { x: 75, y: 0 }, { x: 100, y: 50 },
      { x: 75, y: 100 }, { x: 25, y: 100 }, { x: 0, y: 50 }
    ],
    frame_pentagon: [
      { x: 50, y: 0 }, { x: 100, y: 38 }, { x: 81, y: 100 },
      { x: 19, y: 100 }, { x: 0, y: 38 }
    ],
    frame_diamond: [{ x: 50, y: 0 }, { x: 100, y: 50 }, { x: 50, y: 100 }, { x: 0, y: 50 }],
    frame_star: [
      { x: 50, y: 0 }, { x: 61, y: 35 }, { x: 98, y: 35 }, { x: 68, y: 57 }, { x: 79, y: 91 },
      { x: 50, y: 70 }, { x: 21, y: 91 }, { x: 32, y: 57 }, { x: 2, y: 35 }, { x: 39, y: 35 }
    ],
    frame_ticket: [
      { x: 0, y: 10 }, { x: 10, y: 0 }, { x: 90, y: 0 }, { x: 100, y: 10 },
      { x: 100, y: 90 }, { x: 90, y: 100 }, { x: 10, y: 100 }, { x: 0, y: 90 }
    ]
  };

  if (polygonMap[frameId]) return { type: "polygon" as const, points: polygonMap[frameId] };

  // SVG frames: ignore clipping in Konva preview for now
  return null;
}

/**
 * Ensure every element has a stable id (string).
 * This modifies the object by cloning and assigning ids where missing.
 */
function ensureElementIds(docData: any): any {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  let changed = false;

  const newPages = pages.map((p: any) => {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    const newEls = els.map((el: any, idx: number) => {
      if (el?.id) return el;
      changed = true;
      return { ...el, id: `el_${Math.random().toString(16).slice(2)}_${idx}` };
    });
    return { ...p, elements: newEls };
  });

  return changed ? { ...docData, pages: newPages } : docData;
}

function findElementWithPageIndex(docData: any, elementId: string) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex];
    const elements = Array.isArray(page?.elements) ? page.elements : [];
    for (const el of elements) {
      if (el?.id === elementId) return { el, pageIndex };
    }
  }
  return null;
}

function isCornerAnchor(anchor?: string | null) {
  return anchor === "top-left" || anchor === "top-right" || anchor === "bottom-left" || anchor === "bottom-right";
}

function findSelectedType(docData: any, selectedId: string | null) {
  if (!selectedId) return null;
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (const page of pages) {
    const elements = Array.isArray(page?.elements) ? page.elements : [];
    for (const el of elements) {
      if (el?.id === selectedId) return el?.type || null;
    }
  }
  return null;
}

// Render list bullets in preview without modifying stored text.
function renderTextForPreview(el: any) {
  const role = String(el?.text_role || "body");
  const raw = String(el?.text || "");

  if (role !== "list") return raw;

  const marker = String(el?.list_marker || "bullet");
  const lines = raw.split("\n");

  if (marker === "number") {
    let index = 1;
    return lines
      .map((line) => (line.trim().length ? `${index++}. ${line}` : line))
      .join("\n");
  }

  return lines
    .map((line) => (line.trim().length ? `• ${line}` : line))
    .join("\n");
}

// Convert a polygon preset in 0..100 coordinates into Konva points for a box.
function shapePointsToPx(shapeId: string, wPx: number, hPx: number) {
  const preset = SHAPE_PRESETS[shapeId as keyof typeof SHAPE_PRESETS];
  if (!preset || preset.kind !== "polygon") return [];
  return preset.points.flatMap((point) => [(point.x / 100) * wPx, (point.y / 100) * hPx]);
}

// Build a Konva clipFunc for shape-based image masks.
function makeShapeClipFunc({
  shapeId,
  wPx,
  hPx
}: {
  shapeId: string;
  wPx: number;
  hPx: number;
}): (ctx: CanvasRenderingContext2D) => void | [Path2D, CanvasFillRule] {
  const preset = SHAPE_PRESETS[shapeId as keyof typeof SHAPE_PRESETS] || SHAPE_PRESETS.rectangle;

  return (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();

    if (preset.kind === "rect") {
      ctx.rect(0, 0, wPx, hPx);
      return;
    }

    if (preset.kind === "circle") {
      if (typeof ctx.ellipse === "function") {
        ctx.ellipse(wPx / 2, hPx / 2, wPx / 2, hPx / 2, 0, 0, Math.PI * 2);
      } else {
        const r = Math.min(wPx, hPx) / 2;
        ctx.arc(wPx / 2, hPx / 2, r, 0, Math.PI * 2);
      }
      return;
    }

    if (preset.kind === "polygon") {
      const points = preset.points;
      ctx.moveTo((points[0].x / 100) * wPx, (points[0].y / 100) * hPx);
      for (let index = 1; index < points.length; index++) {
        ctx.lineTo((points[index].x / 100) * wPx, (points[index].y / 100) * hPx);
      }
      ctx.closePath();
      return;
    }

    if (preset.kind === "path" && typeof window !== "undefined" && typeof (window as any).Path2D === "function") {
      const Path2DClass = (window as any).Path2D;
      const DOMMatrixClass = (window as any).DOMMatrix;
      if (typeof DOMMatrixClass === "function") {
        const path = new Path2DClass(preset.d);
        const scaled = new Path2DClass();
        scaled.addPath(path, new DOMMatrixClass([wPx / 100, 0, 0, hPx / 100, 0, 0]));
        return [scaled, (preset.fillRule || "nonzero") as CanvasFillRule];
      }
    }

    ctx.rect(0, 0, wPx, hPx);
  };
}

type CropSession = null | {
  doc: "content" | "cover";
  elementId: string;
  prev: {
    image_mode?: string;
    fit?: string;
    scale?: number;
    pan_mm?: { x: number; y: number };
    rotate_deg?: number;
  };
};

const htmlImageCache = new Map<string, HTMLImageElement | null>();

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  const cached = htmlImageCache.get(src);
  if (cached) return Promise.resolve(cached);

  return new Promise((resolve, reject) => {
    const i = new window.Image();
    i.crossOrigin = "anonymous";
    i.onload = () => {
      htmlImageCache.set(src, i);
      resolve(i);
    };
    i.onerror = () => {
      htmlImageCache.set(src, null);
      reject(new Error(`image load failed: ${src}`));
    };
    i.src = src;
  });
}

export async function preloadCanvasImageUrls(
  urls: string[],
  onProgress?: (done: number, total: number) => void
) {
  const unique = Array.from(new Set(urls.filter(Boolean)));
  if (unique.length === 0) {
    onProgress?.(0, 0);
    return;
  }

  let done = 0;
  onProgress?.(0, unique.length);

  await Promise.allSettled(
    unique.map(async (url) => {
      try {
        await loadHtmlImage(url);
      } finally {
        done += 1;
        onProgress?.(done, unique.length);
      }
    })
  );
}

function getElementImageUrls(elements: any[]) {
  const urls: string[] = [];
  for (const el of elements || []) {
    if (el?.type !== "image" && el?.type !== "background") continue;
    if (el?.image_url) urls.push(String(el.image_url));
    if (el?.type === "image" && el?.source === "clipart" && el?.clipart_id) {
      urls.push(getClipartPublicUrl(el.clipart_id));
    }
  }
  return urls;
}

function useHtmlImage(src?: string) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!src) {
      setImg(null);
      return;
    }
    const cached = htmlImageCache.get(src);
    if (cached) {
      setImg(cached);
      return;
    }

    let cancelled = false;
    setImg(null);
    loadHtmlImage(src)
      .then((loaded) => {
        if (!cancelled) setImg(loaded);
      })
      .catch(() => {
        if (!cancelled) setImg(null);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return img;
}

function getClipartPublicUrl(clipartId?: string) {
  const clean = String(clipartId || "")
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");
  return clean ? `/clipart/${clean}` : "";
}

function ToolBtn({
  x,
  label,
  onClick,
  wide,
}: {
  x: number;
  label: string;
  onClick: () => void;
  wide?: boolean;
}) {
  const width = wide ? 60 : 34;

  return (
    <Group
      x={x}
      onClick={(e) => {
        e.cancelBubble = true;
        onClick();
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        onClick();
      }}
    >
      <Rect width={width} height={24} fill="#ffffff" opacity={0.12} cornerRadius={8} />
      <Text x={0} y={4} width={width} align="center" text={label} fontSize={12} fill="#ffffff" listening={false} />
    </Group>
  );
}

// Small image toolbar shown under the image when selected.
function ImageToolbar({
  wPx,
  hPx,
  cropMode,
  onZoomOut,
  onZoomIn,
  onCrop,
  onDone,
  onCancel,
  onRemove
}: {
  wPx: number;
  hPx: number;
  cropMode?: boolean;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onCrop: () => void;
  onDone?: () => void;
  onCancel?: () => void;
  onRemove: () => void;
}) {
  const pad = 6;
  const btnW = 34;
  const btnH = 24;
  const gap = 6;

  const y = hPx + 10;
  const x = 0;

  const containerW = cropMode ? pad * 2 + 60 * 2 + gap : pad * 2 + btnW * 4 + gap * 3 + 26;

  return (
    <Group x={x} y={y}>
      <Rect width={containerW} height={btnH + pad * 2} fill="#111827" opacity={0.85} cornerRadius={10} />
      <Group x={pad} y={pad}>
        {cropMode ? (
          <>
            <ToolBtn x={0} label="Done" onClick={onDone || (() => {})} wide />
            <ToolBtn x={60 + gap} label="Cancel" onClick={onCancel || (() => {})} wide />
          </>
        ) : (
          <>
            <ToolBtn x={0} label="−" onClick={onZoomOut} />
            <ToolBtn x={btnW + gap} label="+" onClick={onZoomIn} />
            <ToolBtn x={(btnW + gap) * 2} label="Crop" onClick={onCrop} wide />
            <ToolBtn x={(btnW + gap) * 2 + 60 + gap} label="✕" onClick={onRemove} />
          </>
        )}
      </Group>
    </Group>
  );
}

function AnchorArrowBadge({
  x,
  y,
  txt
}: {
  x: number;
  y: number;
  txt: string;
}) {
  const size = 36;
  const half = size / 2;
  const isCorner = txt === "⤡" || txt === "⤢";
  const fontSize = isCorner ? 28 : 24;
  return (
    <Group x={x - half} y={y - half} listening={false}>
      <Rect width={size} height={size} fill="#e5e7eb" cornerRadius={half} />
      <Text x={0} y={isCorner ? 3 : 5} width={size} align="center" text={txt} fontSize={fontSize} fill="#374151" />
    </Group>
  );
}

const ANCHOR_ICON: Record<string, string> = {
  "top-left": "⤡",
  "top-right": "⤢",
  "bottom-left": "⤢",
  "bottom-right": "⤡",
  "top-center": "↕",
  "bottom-center": "↕",
  "middle-left": "↔",
  "middle-right": "↔"
};

const ROTATE_HANDLE_OFFSET_PX = 52;
const SPINE_TITLE_MIN_SPINE_MM = 5;
const SPINE_TITLE_DEFAULT_PADDING_MM = 2;

function positionRotateHandleNode(
  handle: any,
  frameW: number,
  frameH: number,
  rotateDeg: number,
  centerX: number,
  centerY: number
) {
  if (!handle) return;
  const r = frameH / 2 + ROTATE_HANDLE_OFFSET_PX;
  const rad = (rotateDeg * Math.PI) / 180;
  handle.x(centerX + r * Math.sin(rad));
  handle.y(centerY - r * Math.cos(rad));
}

function RotateHandle({
  rootRef,
  handleRef,
  frameW,
  frameH,
  rotateDeg,
  onRotate,
  onRotateCommit,
}: {
  rootRef: React.RefObject<any>;
  handleRef?: React.RefObject<any>;
  frameW: number;
  frameH: number;
  rotateDeg: number;
  onRotate: (deg: number) => void;
  onRotateCommit: (deg: number) => void;
}) {
  const startRotationRef = useRef(0);
  const prevAngleRef = useRef(0);
  const accDeltaRef = useRef(0);
  const centerRef = useRef<{ x: number; y: number } | null>(null);
  const R = frameH / 2 + ROTATE_HANDLE_OFFSET_PX;
  const rad = (rotateDeg * Math.PI) / 180;
  const cx = frameW / 2;
  const cy = frameH / 2;
  const hx = cx + R * Math.sin(rad);
  const hy = cy - R * Math.cos(rad);

  return (
    <Group
      ref={handleRef}
      x={hx}
      y={hy}
      onMouseDown={(e) => {
        e.cancelBubble = true;

        const stage = e.target.getStage();
        const root = rootRef.current;
        if (!stage || !root) return;

        const p0 = stage.getPointerPosition();
        if (!p0) return;

        // Compute a stable center using the node's absolute transform.
        // We use the element's local center transformed to stage coordinates.
        const t = root.getAbsoluteTransform().copy();
        const c = t.point({ x: frameW / 2, y: frameH / 2 });
        centerRef.current = { x: c.x, y: c.y };

        const startAngle = (Math.atan2(p0.y - c.y, p0.x - c.x) * 180) / Math.PI;

        startRotationRef.current = Number(rotateDeg || 0);
        prevAngleRef.current = startAngle;
        accDeltaRef.current = 0;

        let lastNext = ((startRotationRef.current % 360) + 360) % 360;

        const move = (evt?: any) => {
          const p = stage.getPointerPosition();
          const center = centerRef.current;
          if (!p || !center) return;

          const angleNow = (Math.atan2(p.y - center.y, p.x - center.x) * 180) / Math.PI;

          let deltaDeg = angleNow - prevAngleRef.current;
          if (deltaDeg > 180) deltaDeg -= 360;
          if (deltaDeg < -180) deltaDeg += 360;

          accDeltaRef.current += deltaDeg;
          prevAngleRef.current = angleNow;

          let next = startRotationRef.current + accDeltaRef.current;

          // Snap rotation to 15 degrees while Shift is held.
          const shift = Boolean(evt?.evt?.shiftKey || evt?.shiftKey);
          if (shift) {
            next = Math.round(next / 15) * 15;
          }

          next = ((next % 360) + 360) % 360;

          lastNext = next;
          onRotate(next);
          stage.batchDraw();
        };

        const stop = () => {
          onRotateCommit(lastNext);
          centerRef.current = null;

          stage.off("mousemove.rot", move);
          stage.off("mouseup.rot", stop);
          stage.off("mouseleave.rot", stop);
        };

        stage.on("mousemove.rot", move);
        stage.on("mouseup.rot", stop);
        stage.on("mouseleave.rot", stop);
      }}
      onTap={(e) => {
        e.cancelBubble = true;
      }}
    >
      <Circle radius={24} fill="#e5e7eb" />
      <Text x={-24} y={-16} width={48} align="center" text="⟲" fontSize={32} fill="#374151" listening={false} />
    </Group>
  );
}

function RectElement({
  doc,
  el,
  idx,
  pageIndex,
  snapTargets,
  liveSnapMove,
  snapResizeBox,
  marginBoxesMm,
  pageWmm,
  xPx,
  yPx,
  wPx,
  hPx,
  isSelected,
  registerNode,
  onFocusPage,
  onElementSelect,
}: {
  doc: "content" | "cover";
  el: any;
  idx: number;
  pageIndex: number;
  snapTargets: { xs: number[]; ys: number[] };
  liveSnapMove: (args: any) => { xMm: number; yMm: number; xTargetMm: number | null; yTargetMm: number | null };
  snapResizeBox: (args: any) => { xMm: number; yMm: number; wMm: number; hMm: number; xGuideMm: number | null; yGuideMm: number | null };
  marginBoxesMm: Array<{ x: number; y: number; w: number; h: number }>;
  pageWmm: number;
  xPx: number;
  yPx: number;
  wPx: number;
  hPx: number;
  isSelected: boolean;
  registerNode: (id: string, node: any) => void;
  onFocusPage?: (pageIndex: number) => void;
  onElementSelect?: () => void;
}) {
  const store = useEditorStore();
  const groupRef = useRef<any>(null);
  const rotateHandleWrapRef = useRef<any>(null);
  const key = el?.id || `rect_${idx}`;
  const rotate = Number(el.rotate_deg ?? 0);
  const style = el.style || {};
  const fill = style.fill_hex || "#ff0000";
  const backgroundKind = String(style.background_kind || "solid");
  const patternId = String(style.pattern_id || "");
  const patternScale = Math.max(0.25, Math.min(6, Number(style.pattern_scale || 1)));
  const patternOpacity = Math.max(0, Math.min(1, Number(style.pattern_opacity ?? 1)));
  const patternDef = backgroundKind === "pattern" ? getBackgroundPatternById(patternId) : null;
  const patternImg = useHtmlImage(patternDef ? getBackgroundPatternUrl(patternDef.fileName) : "");

  useEffect(() => {
    if (el?.id && groupRef.current) registerNode(el.id, groupRef.current);
    return () => {
      if (el?.id) registerNode(el.id, null);
    };
  }, [el?.id, registerNode]);

  function updateLiveRotateHandle(node: any) {
    const sx = Math.abs(Number(node.scaleX?.() ?? 1));
    const sy = Math.abs(Number(node.scaleY?.() ?? 1));
    const nextWpx = Math.max(1, wPx * sx);
    const nextHpx = Math.max(1, hPx * sy);
    positionRotateHandleNode(
      rotateHandleWrapRef.current,
      nextWpx,
      nextHpx,
      Number(node.rotation?.() ?? rotate),
      Number(node.x?.() ?? nextWpx / 2),
      Number(node.y?.() ?? nextHpx / 2)
    );
    node.getLayer()?.batchDraw();
  }

  function commitTransform(node: any) {
    if (!node || !el?.id) return;

    const sx = Math.abs(Number(node.scaleX?.() ?? 1));
    const sy = Math.abs(Number(node.scaleY?.() ?? 1));
    const newWpx = Math.max(1, wPx * sx);
    const newHpx = Math.max(1, hPx * sy);

    let patch = {
      x_mm: pxToMm(xPx + node.x() - newWpx / 2),
      y_mm: pxToMm(yPx + node.y() - newHpx / 2),
      w_mm: pxToMm(newWpx),
      h_mm: pxToMm(newHpx),
      rotate_deg: Number(node.rotation?.() ?? rotate)
    };

    const snapped = snapResizeBox({
      xMm: patch.x_mm,
      yMm: patch.y_mm,
      wMm: patch.w_mm,
      hMm: patch.h_mm
    });
    patch = {
      ...patch,
      x_mm: snapped.xMm,
      y_mm: snapped.yMm,
      w_mm: snapped.wMm,
      h_mm: snapped.hMm
    };

    const snappedWpx = mmToPx(patch.w_mm);
    const snappedHpx = mmToPx(patch.h_mm);

    node.scaleX(1);
    node.scaleY(1);
    node.offsetX(snappedWpx / 2);
    node.offsetY(snappedHpx / 2);
    node.x(mmToPx(patch.x_mm) - xPx + snappedWpx / 2);
    node.y(mmToPx(patch.y_mm) - yPx + snappedHpx / 2);
    node.getLayer()?.batchDraw();

    store.updateElement(doc, el.id, patch);
  }

  return (
    <Group
      key={key}
      x={xPx}
      y={yPx}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true;
        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onDragMove={(e) => {
        const node = e.target;
        const snapped = liveSnapMove({
          node,
          xMm: pxToMm(node.x()),
          yMm: pxToMm(node.y()),
          wMm: Number(el.w_mm ?? 0),
          hMm: Number(el.h_mm ?? 0),
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2
        });

        if (doc === "content") {
          const box = pickMarginBoxMm(marginBoxesMm, pageWmm, snapped.xMm, Number(el.w_mm ?? 0));
          if (box) {
            const clamped = clampToBoxMm({
              xMm: snapped.xMm,
              yMm: snapped.yMm,
              wMm: Number(el.w_mm ?? 0),
              hMm: Number(el.h_mm ?? 0),
              box
            });

            node.x(mmToPx(clamped.xMm));
            node.y(mmToPx(clamped.yMm));
            node.getLayer()?.batchDraw();
          }
        }
      }}
      onDragEnd={(e) => {
        const node = e.target;
        let snapped = liveSnapMove({
          node,
          xMm: pxToMm(node.x()),
          yMm: pxToMm(node.y()),
          wMm: Number(el.w_mm ?? 0),
          hMm: Number(el.h_mm ?? 0),
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2
        });
        store.updateElement(doc, el.id, {
          x_mm: snapped.xMm,
          y_mm: snapped.yMm,
        });
      }}
    >
      <Group
        ref={groupRef}
        id={el.id}
        x={wPx / 2}
        y={hPx / 2}
        offsetX={wPx / 2}
        offsetY={hPx / 2}
        rotation={rotate}
        onTransform={(e) => {
          updateLiveRotateHandle(e.target);
        }}
        onTransformEnd={(e) => {
          commitTransform(e.target);
        }}
      >
        <Rect x={0} y={0} width={wPx} height={hPx} fill={fill} />
        {patternImg ? (
          <Rect
            x={0}
            y={0}
            width={wPx}
            height={hPx}
            fillPatternImage={patternImg}
            fillPatternRepeat="repeat"
            fillPatternScale={{ x: patternScale, y: patternScale }}
            opacity={patternOpacity}
            listening={false}
          />
        ) : null}
      </Group>

      {isSelected ? (
        <RotateHandle
          rootRef={groupRef}
          handleRef={rotateHandleWrapRef}
          frameW={wPx}
          frameH={hPx}
          rotateDeg={rotate}
          onRotate={(deg) => store.updateElementTransient(doc, el.id, { rotate_deg: deg })}
          onRotateCommit={(deg) => store.updateElement(doc, el.id, { rotate_deg: deg })}
        />
      ) : null}
    </Group>
  );
}

function ShapeElement({
  doc,
  el,
  idx,
  pageIndex,
  snapTargets,
  liveSnapMove,
  snapResizeBox,
  xPx,
  yPx,
  wPx,
  hPx,
  isSelected,
  registerNode,
  onFocusPage,
  onElementSelect,
}: {
  doc: "content" | "cover";
  el: any;
  idx: number;
  pageIndex: number;
  snapTargets: { xs: number[]; ys: number[] };
  liveSnapMove: (args: any) => { xMm: number; yMm: number; xTargetMm: number | null; yTargetMm: number | null };
  snapResizeBox: (args: any) => { xMm: number; yMm: number; wMm: number; hMm: number; xGuideMm: number | null; yGuideMm: number | null };
  xPx: number;
  yPx: number;
  wPx: number;
  hPx: number;
  isSelected: boolean;
  registerNode: (id: string, node: any) => void;
  onFocusPage?: (pageIndex: number) => void;
  onElementSelect?: () => void;
}) {
  const store = useEditorStore();
  const groupRef = useRef<any>(null);
  const rotateHandleWrapRef = useRef<any>(null);
  const key = el?.id || `shape_${idx}`;
  const rotate = Number(el.rotate_deg ?? 0);
  const shapeId = String(el.shape_id || "rectangle");
  const preset = SHAPE_PRESETS[shapeId as keyof typeof SHAPE_PRESETS] || SHAPE_PRESETS.rectangle;
  const fill = String(el.style?.fill_hex || "#111827");
  const stroke = String(el.style?.stroke_hex || "#111827");
  const strokeWidth = mmToPx(Number(el.style?.stroke_width_mm ?? 0));
  const opacity = el.opacity == null ? 1 : clamp(Number(el.opacity), 0, 1);
  const polygonPoints = shapePointsToPx(shapeId, wPx, hPx);

  useEffect(() => {
    if (el?.id && groupRef.current) registerNode(el.id, groupRef.current);
    return () => {
      if (el?.id) registerNode(el.id, null);
    };
  }, [el?.id, registerNode]);

  function updateLiveRotateHandle(node: any) {
    const sx = Math.abs(Number(node.scaleX?.() ?? 1));
    const sy = Math.abs(Number(node.scaleY?.() ?? 1));
    const nextWpx = Math.max(1, wPx * sx);
    const nextHpx = Math.max(1, hPx * sy);
    positionRotateHandleNode(
      rotateHandleWrapRef.current,
      nextWpx,
      nextHpx,
      Number(node.rotation?.() ?? rotate),
      Number(node.x?.() ?? nextWpx / 2),
      Number(node.y?.() ?? nextHpx / 2)
    );
    node.getLayer()?.batchDraw();
  }

  function commitTransform(node: any) {
    if (!node || !el?.id) return;

    const sx = Math.abs(Number(node.scaleX?.() ?? 1));
    const sy = Math.abs(Number(node.scaleY?.() ?? 1));
    const newWpx = Math.max(1, wPx * sx);
    const newHpx = Math.max(1, hPx * sy);

    let patch = {
      x_mm: pxToMm(xPx + node.x() - newWpx / 2),
      y_mm: pxToMm(yPx + node.y() - newHpx / 2),
      w_mm: pxToMm(newWpx),
      h_mm: pxToMm(newHpx),
      rotate_deg: Number(node.rotation?.() ?? rotate)
    };

    const snapped = snapResizeBox({
      xMm: patch.x_mm,
      yMm: patch.y_mm,
      wMm: patch.w_mm,
      hMm: patch.h_mm
    });
    patch = {
      ...patch,
      x_mm: snapped.xMm,
      y_mm: snapped.yMm,
      w_mm: snapped.wMm,
      h_mm: snapped.hMm
    };

    const snappedWpx = mmToPx(patch.w_mm);
    const snappedHpx = mmToPx(patch.h_mm);

    node.scaleX(1);
    node.scaleY(1);
    node.offsetX(snappedWpx / 2);
    node.offsetY(snappedHpx / 2);
    node.x(mmToPx(patch.x_mm) - xPx + snappedWpx / 2);
    node.y(mmToPx(patch.y_mm) - yPx + snappedHpx / 2);
    node.getLayer()?.batchDraw();

    store.updateElement(doc, el.id, patch);
  }

  return (
    <Group
      key={key}
      x={xPx}
      y={yPx}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true;
        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onDragMove={(e) => {
        const node = e.target;
        liveSnapMove({
          node,
          xMm: pxToMm(node.x()),
          yMm: pxToMm(node.y()),
          wMm: Number(el.w_mm ?? 0),
          hMm: Number(el.h_mm ?? 0),
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2
        });
      }}
      onDragEnd={(e) => {
        const node = e.target;
        const snapped = liveSnapMove({
          node,
          xMm: pxToMm(node.x()),
          yMm: pxToMm(node.y()),
          wMm: Number(el.w_mm ?? 0),
          hMm: Number(el.h_mm ?? 0),
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2
        });
        store.updateElement(doc, el.id, {
          x_mm: snapped.xMm,
          y_mm: snapped.yMm,
        });
      }}
    >
      <Group
        ref={groupRef}
        id={el.id}
        x={wPx / 2}
        y={hPx / 2}
        offsetX={wPx / 2}
        offsetY={hPx / 2}
        rotation={rotate}
        onTransform={(e) => {
          updateLiveRotateHandle(e.target);
        }}
        onTransformEnd={(e) => {
          commitTransform(e.target);
        }}
      >
        {preset.kind === "rect" ? (
          <Rect
            x={0}
            y={0}
            width={wPx}
            height={hPx}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeScaleEnabled={false}
            opacity={opacity}
          />
        ) : preset.kind === "circle" ? (
          <Ellipse
            x={wPx / 2}
            y={hPx / 2}
            radiusX={wPx / 2}
            radiusY={hPx / 2}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeScaleEnabled={false}
            opacity={opacity}
          />
        ) : preset.kind === "path" ? (
          (() => {
            const sx = wPx / 100;
            const sy = hPx / 100;
            const strokeAdj = Math.max(0.0001, Math.min(sx, sy));
            const strokePx = strokeWidth / strokeAdj;

            return (
              <Group x={0} y={0}>
                <Group x={0} y={0} scaleX={sx} scaleY={sy}>
                  <Path
                    data={preset.d}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokePx}
                    opacity={opacity}
                    fillRule={preset.fillRule || "nonzero"}
                  />
                </Group>
              </Group>
            );
          })()
        ) : (
          <Line
            points={polygonPoints}
            closed
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeScaleEnabled={false}
            opacity={opacity}
            lineJoin="round"
          />
        )}
      </Group>

      {isSelected ? (
        <RotateHandle
          rootRef={groupRef}
          handleRef={rotateHandleWrapRef}
          frameW={wPx}
          frameH={hPx}
          rotateDeg={rotate}
          onRotate={(deg) => store.updateElementTransient(doc, el.id, { rotate_deg: deg })}
          onRotateCommit={(deg) => store.updateElement(doc, el.id, { rotate_deg: deg })}
        />
      ) : null}
    </Group>
  );
}

function SpineTitleOverlay({
  coverMeta,
  spineBox,
}: {
  coverMeta: any;
  spineBox: { x: number; y: number; w: number; h: number };
}) {
  const textRef = useRef<any>(null);
  const title = coverMeta?.spine_title || null;
  const text = String(title?.text || "").trim();
  const enabled = Boolean(title?.enabled);
  const spineMm = Number(spineBox?.w || coverMeta?.spine_mm || 0);
  const paddingMm = clamp(Number(title?.padding_mm ?? SPINE_TITLE_DEFAULT_PADDING_MM), 0, 10);
  const usableSpineMm = Math.max(0, Number(spineMm || 0) - paddingMm * 2);
  const usableHeightMm = Math.max(1, Number(spineBox?.h || 0) - paddingMm * 2);
  const fontSizePt = Number(title?.font_size_pt || 0);
  const family = String(title?.font_family || "Raleway");
  const weight = Number(title?.font_weight ?? 700);
  const italic = Boolean(title?.italic);
  const fontStyle = weight >= 600 && italic ? "bold italic" : weight >= 600 ? "bold" : italic ? "italic" : "normal";

  useEffect(() => {
    if (!text || typeof document === "undefined") return;
    const fonts = (document as any).fonts;
    if (!fonts?.load) return;
    const cssFamily = family.includes(" ") ? `"${family}"` : family;
    const cssStyle = italic ? "italic" : "normal";
    const cssWeight = weight >= 600 ? 700 : 400;
    let cancelled = false;
    fonts.load(`${cssStyle} ${cssWeight} ${Math.max(8, fontSizePt)}px ${cssFamily}`).then(() => {
      if (cancelled) return;
      textRef.current?.getLayer?.()?.batchDraw?.();
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [family, fontSizePt, italic, text, weight]);

  if (!enabled || !text || Number(spineMm || 0) < SPINE_TITLE_MIN_SPINE_MM || usableSpineMm <= 0 || fontSizePt <= 0) {
    return null;
  }

  const boxWpx = mmToPx(usableHeightMm);
  const boxHpx = mmToPx(usableSpineMm);
  const cx = mmToPx(Number(spineBox?.x || 0) + Number(spineBox?.w || 0) / 2);
  const cy = mmToPx(Number(spineBox?.y || 0) + Number(spineBox?.h || 0) / 2);

  return (
    <Group
      x={cx}
      y={cy}
      offsetX={boxWpx / 2}
      offsetY={boxHpx / 2}
      rotation={Number(title?.rotate_deg ?? -90)}
      listening={false}
    >
      <Text
        ref={textRef}
        x={0}
        y={0}
        width={boxWpx}
        height={boxHpx}
        text={text}
        align="center"
        verticalAlign="middle"
        wrap="none"
        ellipsis
        fontFamily={family}
        fontStyle={fontStyle}
        fontSize={ptToPx(fontSizePt)}
        fill={String(title?.color_hex || "#111827")}
        listening={false}
      />
    </Group>
  );
}

function ImagePlaceholderThumb(props: {
  wPx: number;
  hPx: number;
  label?: string;
}) {
  const { wPx, hPx } = props;
  const label = props.label || "ZDJĘCIE";
  const pad = Math.max(8, Math.min(wPx, hPx) * 0.06);
  const innerX = pad;
  const innerY = pad;
  const innerW = Math.max(1, wPx - pad * 2);
  const innerH = Math.max(1, hPx - pad * 2);
  const iconCx = innerX + innerW * 0.28;
  const iconCy = innerY + innerH * 0.3;
  const iconR = Math.max(6, Math.min(innerW, innerH) * 0.08);
  const strokeW = Math.max(2, Math.min(wPx, hPx) * 0.02);
  const hillPoints = [
    innerX + innerW * 0.14,
    innerY + innerH * 0.72,
    innerX + innerW * 0.34,
    innerY + innerH * 0.48,
    innerX + innerW * 0.49,
    innerY + innerH * 0.62,
    innerX + innerW * 0.63,
    innerY + innerH * 0.5,
    innerX + innerW * 0.86,
    innerY + innerH * 0.78,
  ];

  return (
    <Group>
      <Rect
        x={0}
        y={0}
        width={wPx}
        height={hPx}
        fill="#F3F4F6"
        cornerRadius={12}
      />
      <Circle x={iconCx} y={iconCy} radius={iconR} fill="#CBD5E1" listening={false} />
      <Line
        points={hillPoints}
        stroke="#CBD5E1"
        strokeWidth={strokeW}
        lineCap="round"
        lineJoin="round"
        listening={false}
      />
      <Text
        x={innerX}
        y={innerY + innerH * 0.78}
        width={innerW}
        align="center"
        text={label}
        fill="#94A3B8"
        fontStyle="bold"
        fontSize={Math.max(11, Math.min(wPx, hPx) * 0.08)}
        listening={false}
      />
    </Group>
  );
}

/**
 * ImageElement uses a dedicated frame Rect as Transformer target.
 * We keep local frame size (px) to make resize live and stable.
 */
function ImageElement({
  doc,
  el,
  idx,
  pageIndex,
  pageWidthMm,
  snapTargets,
  liveSnapMove,
  snapResizeBox,
  wPx,
  hPx,
  xPx,
  yPx,
  isSelected,
  registerNode,
  cropSession,
  startCrop,
  doneCrop,
  cancelCrop,
  onFocusPage,
  onElementSelect,
  onImagePlaceholderDoubleClick,
}: {
  doc: "content" | "cover";
  el: any;
  idx: number;
  pageIndex: number;
  pageWidthMm: number;
  snapTargets: { xs: number[]; ys: number[] };
  liveSnapMove: (args: any) => { xMm: number; yMm: number; xTargetMm: number | null; yTargetMm: number | null };
  snapResizeBox: (args: any) => { xMm: number; yMm: number; wMm: number; hMm: number; xGuideMm: number | null; yGuideMm: number | null };
  wPx: number;
  hPx: number;
  xPx: number;
  yPx: number;
  isSelected: boolean;
  registerNode: (id: string, node: any) => void;
  cropSession: CropSession;
  startCrop: (doc: "content" | "cover", elementId: string, el: any) => void;
  doneCrop: () => void;
  cancelCrop: () => void;
  onFocusPage?: (pageIndex: number) => void;
  onElementSelect?: () => void;
  onImagePlaceholderDoubleClick?: (payload: { doc: "content" | "cover"; pageIndex: number; elementId: string }) => void;
}) {
  const store = useEditorStore();

  const groupRef = useRef<any>(null);
  const contentRef = useRef<any>(null);
  const frameRef = useRef<any>(null);
  const clipGroupRef = useRef<any>(null);
  const selectionRef = useRef<any>(null);
  const imgNodeRef = useRef<any>(null);
  const rafRef = useRef<number | null>(null);
  const lastPatchRef = useRef<any>(null);
  // Track live resize to avoid Konva cache issues during transform
  const [isResizing, setIsResizing] = useState(false);

  // Local frame size in px for live resize feedback
  const [frameW, setFrameW] = useState(wPx);
  const [frameH, setFrameH] = useState(hPx);

  // Sync local frame size when store-driven size changes (resume/autosave)
  useEffect(() => {
    setFrameW(wPx);
    setFrameH(hPx);
  }, [wPx, hPx]);

  const clipartUrl = el.source === "clipart" && el.clipart_id ? getClipartPublicUrl(el.clipart_id) : "";
  const imageUrl = el.image_url || clipartUrl || null;
  const img = useHtmlImage(imageUrl || undefined);
  const hasAsset = Number(el.asset_id || 0) > 0;
  const hasClipart = Boolean(el.source === "clipart" && el.clipart_id);
  const hasImageUrl = Boolean(imageUrl);
  const hasLoadedImage = Boolean(img);
  // English comment: Empty slots and persisted assets waiting for preview URLs are distinct states.
  const showLoadingImage = (hasAsset || hasClipart || hasImageUrl) && !hasLoadedImage;
  const imagePlaceholderLabel = showLoadingImage ? "LOADING" : "IMAGE";

  const key = el?._ghost_key || el?.id || `${el?.type || "el"}_${idx}`;
  const isGhost = Boolean(el?._ghost);
  const imageMode = String(el.image_mode || "frame");
  const isFreeMode = imageMode === "free";
  const isCroppingThis = Boolean(cropSession && cropSession.doc === doc && cropSession.elementId === el.id);
  const isImagePlaceholder = Boolean(el.placeholder) || String(el.placeholder_kind || "") === "image";
  /**
   * Auto-detect spread behavior without requiring a checkbox.
   * Rules:
   * - Left page (odd index): if x+w > pageW => spread=true
   * - Right page (even index): if x < 0 => move to left and spread=true
   * - If spread=true and element fully fits within a single page => spread=false
   */
  function autoSpreadNormalize(nextXmm: number, nextWmm: number) {
    if (doc !== "content") return;
    if (!el?.id) return;
    if (String(store.contentData?.meta?.layout_mode || "pages") === "book_spreads") return;

    const pages = Array.isArray(store.contentData?.pages) ? store.contentData.pages : [];
    const n = pages.length;
    if (pageIndex <= 0 || pageIndex >= n - 1) return;

    const pageW = pageWidthMm;
    const x = Number(nextXmm ?? el.x_mm ?? 0);
    const w = Number(nextWmm ?? el.w_mm ?? 0);

    const isRight = pageIndex % 2 === 0;
    const isLeft = !isRight;

    // If element is on right page and crosses gutter (x<0), move to left
    if (isRight && x < 0) {
      store.updateElement("content", el.id, { x_mm: x + pageW, spread: true });
      return;
    }

    // If element is on left page and crosses into right side
    if (isLeft && x + w > pageW) {
      if (!el.spread) store.updateElement("content", el.id, { spread: true });
      return;
    }

    // If currently spread but fits fully within a single page, disable spread
    if (el.spread && isLeft && x >= 0 && x + w <= pageW) {
      store.updateElement("content", el.id, { spread: false });
    }
  }

  // Free mode targets the image node. Frame mode targets the frame rect so crop/zoom overflow does not move handles.
  useEffect(() => {
    if (isGhost) return;
    const targetNode = isFreeMode ? imgNodeRef.current : frameRef.current;
    if (el?.id && targetNode) {
      targetNode.setAttr("imageModeFree", isFreeMode);
      registerNode(el.id, targetNode);
    }
    return () => {
      if (isGhost) return;
      if (el?.id) registerNode(el.id, null);
    };
  }, [el?.id, img, isFreeMode, isGhost, registerNode]);

  // ---- Preview properties ----
  const rotate = Number(el.rotate_deg ?? 0);
  const opacity = el.opacity == null ? 1 : Math.max(0, Math.min(1, Number(el.opacity)));

  const panXmm = Number(el.pan_mm?.x ?? 0);
  const panYmm = Number(el.pan_mm?.y ?? 0);
  const panXPx = mmToPx(panXmm);
  const panYPx = mmToPx(panYmm);

  const fit = el.fit === "cover" ? "cover" : "contain";

  // Preserve aspect ratio (no distortion) using the *current frameW/frameH*
  const nW = img?.naturalWidth || img?.width || 1;
  const nH = img?.naturalHeight || img?.height || 1;

  const baseScale =
    fit === "contain"
      ? Math.min(frameW / nW, frameH / nH)
      : Math.max(frameW / nW, frameH / nH);

  const userScale = Number(el.scale ?? 1);
  const finalScale = baseScale * userScale;

  const imgW = nW * finalScale;
  const imgH = nH * finalScale;

  const imgX = frameW / 2 + panXPx;
  const imgY = frameH / 2 + panYPx;

  /**
   * Clamp pan so the image always covers the frame (no empty edges).
   * Pan is expressed in px as offset from frame center.
   */
  function clampPanPx(panXPxValue: number, panYPxValue: number, drawnW: number, drawnH: number, nextFrameW: number, nextFrameH: number) {
    const maxX = Math.max(0, (drawnW - nextFrameW) / 2);
    const maxY = Math.max(0, (drawnH - nextFrameH) / 2);

    const x = Math.max(-maxX, Math.min(maxX, panXPxValue));
    const y = Math.max(-maxY, Math.min(maxY, panYPxValue));

    return { panXPx: x, panYPx: y };
  }

  // ---- Filters (preset + sliders) ----
  useEffect(() => {
    const node = imgNodeRef.current;
    if (!node) return;

    // During live resize, disable filters to keep preview stable and fast
    if (isResizing) {
      node.filters([]);
      node.clearCache();
      node.getLayer()?.batchDraw();
      return;
    }

    const preset = el.filter?.preset || "none";
    const brightness = Number(el.filter?.brightness ?? 1);
    const contrast = Number(el.filter?.contrast ?? 1);
    const saturation = Number(el.filter?.saturation ?? 1);
    const hasAdjustments = preset !== "none" || brightness !== 1 || contrast !== 1 || saturation !== 1;

    if (!hasAdjustments) {
      node.filters([]);
      node.clearCache();
      node.getLayer()?.batchDraw();
      return;
    }

    const konvaBright = Math.max(-1, Math.min(1, brightness - 1));
    const konvaContrast = Math.max(-100, Math.min(100, (contrast - 1) * 100));
    const konvaSat = Math.max(-1, Math.min(1, saturation - 1));

    const filters: any[] = [];

    if (preset === "grayscale") filters.push(Konva.Filters.Grayscale);
    if (preset === "invert") filters.push(Konva.Filters.Invert);
    if (preset === "sepia") filters.push(Konva.Filters.Sepia);

    // Sliders apply only when adjustments are active.
    filters.push(Konva.Filters.Brighten, Konva.Filters.Contrast, Konva.Filters.HSL);

    // Re-cache to match new geometry (width/height/offset changes)
    node.clearCache();
    node.cache({ pixelRatio: 2 });
    node.filters(filters);

    node.brightness(konvaBright);
    node.contrast(konvaContrast);
    node.saturation(konvaSat);

    node.getLayer()?.batchDraw();
  }, [
    isResizing,
    el.filter?.preset,
    el.filter?.brightness,
    el.filter?.contrast,
    el.filter?.saturation,
    img,
    frameW,
    frameH,
    el.scale,
    el.pan_mm?.x,
    el.pan_mm?.y,
    el.fit
  ]);

  // Use explicit image clip first, then fall back to frame clip presets.
  const clip = (el.clip && el.clip.type && el.clip.type !== "none")
    ? el.clip
    : (isFreeMode ? null : frameToClip(el.frame_id));
  const clipFunc: ((ctx: any) => void | [Path2D, CanvasFillRule]) | undefined = clip
    ? (ctx: any) => {
        if (clip.type === "shape" && clip.shape_id) {
          return makeShapeClipFunc({
            shapeId: String(clip.shape_id),
            wPx: frameW,
            hPx: frameH
          })(ctx);
        }

        ctx.beginPath();
        if (clip.type === "circle") {
          const r = Math.min(frameW, frameH) / 2;
          ctx.arc(frameW / 2, frameH / 2, r, 0, Math.PI * 2);
        } else if (clip.type === "rounded") {
          const r = mmToPx(Number(clip.radius_mm || 0));
          // @ts-ignore
          if (ctx.roundRect) ctx.roundRect(0, 0, frameW, frameH, r);
          else ctx.rect(0, 0, frameW, frameH);
          ctx.closePath();
        } else if (clip.type === "polygon") {
          const pts = clip.points;
          const toX = (p: any) => (p.x / 100) * frameW;
          const toY = (p: any) => (p.y / 100) * frameH;
          ctx.moveTo(toX(pts[0]), toY(pts[0]));
          for (let i = 1; i < pts.length; i++) ctx.lineTo(toX(pts[i]), toY(pts[i]));
          ctx.closePath();
        }
      }
    : undefined;

  /**
   * Apply preview layout imperatively so resize feels "Canva-like".
   * This updates Konva nodes immediately (no waiting for React re-render).
   */
  function applyPreview(nextFrameW: number, nextFrameH: number, nextRotateDeg: number = Number(el.rotate_deg ?? 0)) {
    if (contentRef.current) {
      contentRef.current.setAttrs({
        x: nextFrameW / 2,
        y: nextFrameH / 2,
        offsetX: nextFrameW / 2,
        offsetY: nextFrameH / 2,
        rotation: nextRotateDeg,
      });
    }

    if (frameRef.current) {
      frameRef.current.width(nextFrameW);
      frameRef.current.height(nextFrameH);
    }

    // Update clipping region
    if (clipGroupRef.current) {
      clipGroupRef.current.clipWidth(nextFrameW);
      clipGroupRef.current.clipHeight(nextFrameH);
    }

    // Update selection outline
    if (selectionRef.current) {
      selectionRef.current.width(nextFrameW);
      selectionRef.current.height(nextFrameH);
    }

    // If image node exists, update its size/position based on NEW frameW/frameH
    const node = imgNodeRef.current;
    if (!node || !img) {
      contentRef.current?.getLayer?.()?.batchDraw();
      return;
    }

    if (isFreeMode) {
      node.setAttrs({
        x: 0,
        y: 0,
        width: nextFrameW,
        height: nextFrameH,
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
        opacity: el.opacity == null ? 1 : Math.max(0, Math.min(1, Number(el.opacity))),
      });
      contentRef.current?.getLayer?.()?.batchDraw();
      return;
    }

    const nW2 = img.naturalWidth || img.width || 1;
    const nH2 = img.naturalHeight || img.height || 1;

    const fit2 = el.fit === "contain" ? "contain" : "cover";

    const baseScale2 =
      fit2 === "contain"
        ? Math.min(nextFrameW / nW2, nextFrameH / nH2)
        : Math.max(nextFrameW / nW2, nextFrameH / nH2);

    const userScale2 = Number(el.scale ?? 1);
    const finalScale2 = baseScale2 * userScale2;

    const imgW2 = nW2 * finalScale2;
    const imgH2 = nH2 * finalScale2;

    const panXPx2 = mmToPx(Number(el.pan_mm?.x ?? 0));
    const panYPx2 = mmToPx(Number(el.pan_mm?.y ?? 0));

    const imgX2 = nextFrameW / 2 + panXPx2;
    const imgY2 = nextFrameH / 2 + panYPx2;

    node.setAttrs({
      x: imgX2,
      y: imgY2,
      width: imgW2,
      height: imgH2,
      offsetX: imgW2 / 2,
      offsetY: imgH2 / 2,
      rotation: 0,
      opacity: el.opacity == null ? 1 : Math.max(0, Math.min(1, Number(el.opacity))),
    });

    contentRef.current?.getLayer?.()?.batchDraw();
  }

  function applyFreeImageTransform(commit: boolean) {
    const g = groupRef.current;
    const node = imgNodeRef.current;
    const content = contentRef.current;
    if (!g || !node || !content || !el?.id) return;

    const newWpx = Math.max(10, node.width() * node.scaleX());
    const newHpx = Math.max(10, node.height() * node.scaleY());
    const nextRotateDeg = Number(content.rotation() ?? el.rotate_deg ?? 0);

    let patch = {
      x_mm: pxToMm(g.x() + node.x()),
      y_mm: pxToMm(g.y() + node.y()),
      w_mm: pxToMm(newWpx),
      h_mm: pxToMm(newHpx),
      rotate_deg: nextRotateDeg,
    };

    const snapped = snapResizeBox({
      xMm: patch.x_mm,
      yMm: patch.y_mm,
      wMm: patch.w_mm,
      hMm: patch.h_mm
    });
    patch = {
      x_mm: snapped.xMm,
      y_mm: snapped.yMm,
      w_mm: snapped.wMm,
      h_mm: snapped.hMm,
      rotate_deg: nextRotateDeg,
    };

    g.x(mmToPx(patch.x_mm));
    g.y(mmToPx(patch.y_mm));

    content.position({ x: mmToPx(patch.w_mm) / 2, y: mmToPx(patch.h_mm) / 2 });
    content.offset({ x: mmToPx(patch.w_mm) / 2, y: mmToPx(patch.h_mm) / 2 });
    content.rotation(patch.rotate_deg);
    node.position({ x: 0, y: 0 });
    node.scale({ x: 1, y: 1 });
    node.width(mmToPx(patch.w_mm));
    node.height(mmToPx(patch.h_mm));
    node.offset({ x: 0, y: 0 });
    node.rotation(0);
    node.getLayer()?.batchDraw();

    setFrameW(mmToPx(patch.w_mm));
    setFrameH(mmToPx(patch.h_mm));

    if (commit) {
      store.updateElement(doc, el.id, patch);
      autoSpreadNormalize(patch.x_mm, patch.w_mm);
    } else {
      store.updateElementTransient(doc, el.id, patch);
    }
  }

  function applyFrameTransform(commit: boolean) {
    const g = groupRef.current;
    const c = contentRef.current;
    const r = frameRef.current;
    if (!g || !c || !r || !el?.id) return;

    const sx = r.scaleX();
    const sy = r.scaleY();
    const nextRotateDeg = Number(c.rotation() ?? 0);
    const localDx = Number(r.x() || 0);
    const localDy = Number(r.y() || 0);
    const rad = (nextRotateDeg * Math.PI) / 180;
    const rotatedDx = localDx * Math.cos(rad) - localDy * Math.sin(rad);
    const rotatedDy = localDx * Math.sin(rad) + localDy * Math.cos(rad);

    const newWpx = Math.max(10, r.width() * sx);
    const newHpx = Math.max(10, r.height() * sy);

    const nextOuterX = g.x() + rotatedDx;
    const nextOuterY = g.y() + rotatedDy;

    g.x(nextOuterX);
    g.y(nextOuterY);

    c.position({ x: newWpx / 2, y: newHpx / 2 });
    c.offset({ x: newWpx / 2, y: newHpx / 2 });
    c.scale({ x: 1, y: 1 });
    c.rotation(nextRotateDeg);

    r.position({ x: 0, y: 0 });
    r.scale({ x: 1, y: 1 });
    r.width(newWpx);
    r.height(newHpx);

    let patch = {
      x_mm: pxToMm(g.x()),
      y_mm: pxToMm(g.y()),
      w_mm: pxToMm(newWpx),
      h_mm: pxToMm(newHpx),
      rotate_deg: nextRotateDeg,
    };

    const snapped = snapResizeBox({
      xMm: patch.x_mm,
      yMm: patch.y_mm,
      wMm: patch.w_mm,
      hMm: patch.h_mm
    });
    patch = {
      x_mm: snapped.xMm,
      y_mm: snapped.yMm,
      w_mm: snapped.wMm,
      h_mm: snapped.hMm,
      rotate_deg: nextRotateDeg,
    };

    g.x(mmToPx(patch.x_mm));
    g.y(mmToPx(patch.y_mm));

    // IMPORTANT: update preview immediately (no React wait)
    applyPreview(mmToPx(patch.w_mm), mmToPx(patch.h_mm), patch.rotate_deg);

    // Update local size so the image follows handles immediately
    setFrameW(mmToPx(patch.w_mm));
    setFrameH(mmToPx(patch.h_mm));

    lastPatchRef.current = patch;

    if (commit) {
      store.updateElement(doc, el.id, patch);
      autoSpreadNormalize(patch.x_mm, patch.w_mm);
    } else {
      store.updateElementTransient(doc, el.id, patch);
    }
  }

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    // Re-apply preview when image loads or relevant props change
    applyPreview(frameW, frameH, rotate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameW, frameH, img, isFreeMode, el.fit, el.scale, el.rotate_deg, el.opacity, el.pan_mm?.x, el.pan_mm?.y]);

  return (
    <Group
      key={key}
      ref={groupRef}
      id={el.id}
      x={xPx}
      y={yPx}
      draggable={!isCroppingThis && !isGhost}
      listening={true}
      onMouseDown={(e) => {
        e.cancelBubble = true;

        if (isGhost) {
          // Ghost is shown on the RIGHT page; real element lives on LEFT page
          // Focus left page and select real element
          const leftPage = pageIndex - 1;
          onFocusPage?.(leftPage);
          store.selectElement("content", el.id || null);
          onElementSelect?.();
          return;
        }

        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onTap={(e) => {
        e.cancelBubble = true;

        if (isGhost) {
          const leftPage = pageIndex - 1;
          onFocusPage?.(leftPage);
          store.selectElement("content", el.id || null);
          onElementSelect?.();
          return;
        }

        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onDblClick={(e) => {
        if (!isImagePlaceholder || !el?.id) return;
        e.cancelBubble = true;
        const targetPageIndex = isGhost ? pageIndex - 1 : pageIndex;
        const targetDoc = isGhost ? "content" : doc;
        onFocusPage?.(targetPageIndex);
        store.selectElement(targetDoc, el.id || null);
        onImagePlaceholderDoubleClick?.({ doc: targetDoc, pageIndex: targetPageIndex, elementId: el.id });
      }}
      onDblTap={(e) => {
        if (!isImagePlaceholder || !el?.id) return;
        e.cancelBubble = true;
        const targetPageIndex = isGhost ? pageIndex - 1 : pageIndex;
        const targetDoc = isGhost ? "content" : doc;
        onFocusPage?.(targetPageIndex);
        store.selectElement(targetDoc, el.id || null);
        onImagePlaceholderDoubleClick?.({ doc: targetDoc, pageIndex: targetPageIndex, elementId: el.id });
      }}
      onDragMove={(e) => {
        if (isGhost) return;
        const node = e.target;
        liveSnapMove({
          node,
          xMm: pxToMm(node.x()),
          yMm: pxToMm(node.y()),
          wMm: Number(el.w_mm ?? 0),
          hMm: Number(el.h_mm ?? 0),
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2
        });
      }}
      onDragEnd={(e) => {
        if (isGhost) return;
        const node = e.target;
        const snapped = liveSnapMove({
          node,
          xMm: pxToMm(node.x()),
          yMm: pxToMm(node.y()),
          wMm: Number(el.w_mm ?? 0),
          hMm: Number(el.h_mm ?? 0),
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2
        });
        store.updateElement(doc, el.id, {
          x_mm: snapped.xMm,
          y_mm: snapped.yMm,
        });
        autoSpreadNormalize(snapped.xMm, Number(el.w_mm ?? 0));
      }}
    >
      <Group
        ref={contentRef}
        x={frameW / 2}
        y={frameH / 2}
        offsetX={frameW / 2}
        offsetY={frameH / 2}
        rotation={rotate}
        onTransformStart={() => {
          if (isFreeMode) return;
          setIsResizing(true);
        }}
        onTransform={() => {
          if (isFreeMode) return;
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          rafRef.current = requestAnimationFrame(() => applyFrameTransform(false));
        }}
        onTransformEnd={() => {
          if (isFreeMode) return;
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          applyFrameTransform(true);
          setIsResizing(false);
        }}
      >
        <Rect
          ref={frameRef}
          x={0}
          y={0}
          width={frameW}
          height={frameH}
          opacity={0}
          listening={false}
          onTransformStart={() => {
            if (isFreeMode) return;
            setIsResizing(true);
          }}
          onTransform={() => {
            if (isFreeMode) return;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => applyFrameTransform(false));
          }}
          onTransformEnd={() => {
            if (isFreeMode) return;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            applyFrameTransform(true);
            setIsResizing(false);
          }}
        />

        {isFreeMode ? (
          <Group ref={clipGroupRef} clipFunc={clipFunc} clipWidth={frameW} clipHeight={frameH}>
            {img ? (
              <KonvaImage
                ref={imgNodeRef}
                id={`${el.id}__img`}
                image={img}
                x={0}
                y={0}
                width={frameW}
                height={frameH}
                offsetX={0}
                offsetY={0}
                rotation={0}
                opacity={opacity}
                onTransformStart={() => setIsResizing(true)}
                onTransform={() => {
                  if (rafRef.current) cancelAnimationFrame(rafRef.current);
                  rafRef.current = requestAnimationFrame(() => applyFreeImageTransform(false));
                }}
                onTransformEnd={() => {
                  if (rafRef.current) cancelAnimationFrame(rafRef.current);
                  applyFreeImageTransform(true);
                  setIsResizing(false);
                }}
              />
            ) : (
              <ImagePlaceholderThumb wPx={frameW} hPx={frameH} label={imagePlaceholderLabel} />
            )}
          </Group>
        ) : (
          <Group ref={clipGroupRef} clipFunc={clipFunc} clipWidth={frameW} clipHeight={frameH}>
            {img ? (
              <KonvaImage
                ref={imgNodeRef}
                id={`${el.id}__img`}
                image={img}
                x={imgX}
                y={imgY}
                width={imgW}
                height={imgH}
                offsetX={imgW / 2}
                offsetY={imgH / 2}
                rotation={0}
                opacity={opacity}
                draggable={isCroppingThis}
                onDragMove={(e) => {
                  if (!isCroppingThis) return;
                  const node = e.target;
                  const rawPanXPx = node.x() - frameW / 2;
                  const rawPanYPx = node.y() - frameH / 2;
                  const clamped = clampPanPx(rawPanXPx, rawPanYPx, imgW, imgH, frameW, frameH);

                  node.x(frameW / 2 + clamped.panXPx);
                  node.y(frameH / 2 + clamped.panYPx);

                  store.updateElementTransient(doc, el.id, {
                    pan_mm: {
                      x: pxToMm(clamped.panXPx),
                      y: pxToMm(clamped.panYPx),
                    }
                  });

                  node.getLayer()?.batchDraw();
                }}
                onDragEnd={(e) => {
                  if (!isCroppingThis) return;
                  const node = e.target;
                  const rawPanXPx = node.x() - frameW / 2;
                  const rawPanYPx = node.y() - frameH / 2;
                  const clamped = clampPanPx(rawPanXPx, rawPanYPx, imgW, imgH, frameW, frameH);

                  node.x(frameW / 2 + clamped.panXPx);
                  node.y(frameH / 2 + clamped.panYPx);
                  store.updateElement(doc, el.id, {
                    pan_mm: {
                      x: pxToMm(clamped.panXPx),
                      y: pxToMm(clamped.panYPx),
                    }
                  });
                }}
              />
            ) : (
              <ImagePlaceholderThumb wPx={frameW} hPx={frameH} label={imagePlaceholderLabel} />
            )}
          </Group>
        )}

        {isSelected && !isFreeMode ? (
          <Rect ref={selectionRef} width={frameW} height={frameH} stroke="#111827" strokeWidth={2} fillEnabled={false} />
        ) : null}
      </Group>

      {isSelected && !isGhost && !isCroppingThis ? (
        <RotateHandle
          rootRef={groupRef}
          frameW={frameW}
          frameH={frameH}
          rotateDeg={Number(el.rotate_deg ?? 0)}
          onRotate={(deg) => store.updateElementTransient(doc, el.id, { rotate_deg: deg })}
          onRotateCommit={(deg) => store.updateElement(doc, el.id, { rotate_deg: deg })}
        />
      ) : null}
    </Group>
  );
}

/**
 * TextElement supports:
 * - selection
 * - drag
 * - resize (frame box w/h)
 * - inline editing on double click
 */
function TextElement({
  doc,
  el,
  idx,
  pageIndex,
  snapTargets,
  liveSnapMove,
  snapResizeBox,
  marginBoxesMm,
  pageWmm,
  xPx,
  yPx,
  wPx,
  hPx,
  isSelected,
  isInlineEditing,
  registerNode,
  onInlineEdit,
  onFocusPage,
  onElementSelect,
  transformerRef,
}: {
  doc: "content" | "cover";
  el: any;
  idx: number;
  pageIndex: number;
  snapTargets: { xs: number[]; ys: number[] };
  liveSnapMove: (args: any) => { xMm: number; yMm: number; xTargetMm: number | null; yTargetMm: number | null };
  snapResizeBox: (args: any) => { xMm: number; yMm: number; wMm: number; hMm: number; xGuideMm: number | null; yGuideMm: number | null };
  marginBoxesMm: Array<{ x: number; y: number; w: number; h: number }>;
  pageWmm: number;
  xPx: number;
  yPx: number;
  wPx: number;
  hPx: number;
  isSelected: boolean;
  isInlineEditing: boolean;
  registerNode: (id: string, node: any) => void;
  onInlineEdit: (args: {
    doc: "content" | "cover";
    elementId: string;
    value: string;
    node: any;
    style?: any;
  }) => void;
  onFocusPage?: (pageIndex: number) => void;
  onElementSelect?: () => void;
  transformerRef?: React.RefObject<any>;
}) {
  const store = useEditorStore();
  const groupRef = useRef<any>(null);
  // English comment: Live text box preview during resize (imperative, no React re-render).
  const liveBoxRef = useRef<null | { wPx: number; hPx: number }>(null);
  const textRef = useRef<any>(null);
  const selectionRectRef = useRef<any>(null);
  const guideRectRef = useRef<any>(null);
  const hitRectRef = useRef<any>(null);
  const clipGroupRef = useRef<any>(null);
  const rotateHandleWrapRef = useRef<any>(null);

  // Register the root group as the only Transformer target.
  useEffect(() => {
    if (el?.id && groupRef.current) registerNode(el.id, groupRef.current);
    return () => {
      if (el?.id) registerNode(el.id, null);
    };
  }, [el?.id, registerNode]);

  const key = el?.id || `text_${idx}`;

  const fontSizePt = Number(el.style?.font_size_pt ?? 18);
  const fill = String(el.style?.color_hex || "#000000");
  const family = String(el.style?.font_family || "Raleway");

  const lineHeight = Number(el.style?.line_height ?? 1.2);
  const fw = Number(el.style?.font_weight ?? 400);
  const isBold = fw >= 600;
  const isItalic = Boolean(el.style?.italic);
  const underline = Boolean(el.style?.underline);
  const textShadow = el.style?.text_shadow || {};
  const shadowEnabled = Boolean(textShadow.enabled);
  const shadowColor = String(textShadow.color_hex || "#000000");
  const shadowOpacity = Number(textShadow.opacity ?? 0.35);
  const shadowBlur = Number(textShadow.blur_px ?? 6);
  const shadowOffsetX = Number(textShadow.offset_x_px ?? 0);
  const shadowOffsetY = Number(textShadow.offset_y_px ?? 2);
  const paddingPx = mmToPx(Number(el.style?.padding_mm ?? 0));
  const fontStyle = isBold && isItalic ? "bold italic" : isBold ? "bold" : isItalic ? "italic" : "normal";
  const rotate = Number(el.rotate_deg ?? 0);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const fonts = (document as any).fonts;
    if (!fonts?.load) return;
    const cssFamily = family.includes(" ") ? `"${family}"` : family;
    const cssStyle = isItalic ? "italic" : "normal";
    const cssWeight = isBold ? 700 : 400;
    let cancelled = false;

    fonts.load(`${cssStyle} ${cssWeight} ${Math.max(12, fontSizePt)}px ${cssFamily}`).then(() => {
      if (cancelled) return;
      textRef.current?.getLayer?.()?.batchDraw?.();
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [family, fontSizePt, isBold, isItalic]);

  useEffect(() => {
    /**
     * Auto-start inline edit if requested by store (e.g. after Add text or from Properties).
     */
    const req = store.pendingInlineEdit;
    if (!req) return;
    if (req.doc !== doc) return;
    if (req.elementId !== el.id) return;

    const node = groupRef.current;
    if (!node) return;

    onInlineEdit({
      doc,
      elementId: el.id,
      value: String(el.text || ""),
      node,
      style: el.style || {}
    });

    store.clearInlineEditRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.pendingInlineEdit, doc, el.id, wPx, hPx]);

  // English comment: Keep the outer group scaled by Transformer, while inverse-scaling inner nodes.
  function applyLiveTextPreview(node: any, sx: number, sy: number, nextWpx: number, nextHpx: number) {
    liveBoxRef.current = { wPx: nextWpx, hPx: nextHpx };
    const invSx = sx !== 0 ? 1 / sx : 1;
    const invSy = sy !== 0 ? 1 / sy : 1;

    if (textRef.current) {
      textRef.current.x(0);
      textRef.current.y(0);
      textRef.current.width(nextWpx);
      textRef.current.height(nextHpx);
      textRef.current.scaleX(invSx);
      textRef.current.scaleY(invSy);
    }

    if (selectionRectRef.current) {
      selectionRectRef.current.x(0);
      selectionRectRef.current.y(0);
      selectionRectRef.current.width(nextWpx);
      selectionRectRef.current.height(nextHpx);
      selectionRectRef.current.scaleX(invSx);
      selectionRectRef.current.scaleY(invSy);
    }

    if (guideRectRef.current) {
      guideRectRef.current.x(sx !== 0 ? paddingPx / sx : paddingPx);
      guideRectRef.current.y(sy !== 0 ? paddingPx / sy : paddingPx);
      guideRectRef.current.width(Math.max(1, nextWpx - paddingPx * 2));
      guideRectRef.current.height(Math.max(1, nextHpx - paddingPx * 2));
      guideRectRef.current.scaleX(invSx);
      guideRectRef.current.scaleY(invSy);
    }

    if (hitRectRef.current) {
      hitRectRef.current.x(0);
      hitRectRef.current.y(0);
      hitRectRef.current.width(nextWpx);
      hitRectRef.current.height(nextHpx);
      hitRectRef.current.scaleX(invSx);
      hitRectRef.current.scaleY(invSy);
    }

    if (clipGroupRef.current) {
      clipGroupRef.current.clipWidth(nextWpx);
      clipGroupRef.current.clipHeight(nextHpx);
    }

    positionRotateHandleNode(
      rotateHandleWrapRef.current,
      nextWpx,
      nextHpx,
      Number(node?.rotation?.() ?? rotate),
      Number(node?.x?.() ?? nextWpx / 2),
      Number(node?.y?.() ?? nextHpx / 2)
    );

    const tr = transformerRef?.current;
    if (tr?.forceUpdate) tr.forceUpdate();
    const layer = groupRef.current?.getLayer?.();
    if (layer) layer.draw();
  }

  function commitTransform(node: any) {
    if (!node || !el?.id) return;

    const sx = Math.abs(Number(node.scaleX?.() ?? 1));
    const sy = Math.abs(Number(node.scaleY?.() ?? 1));
    const newWpx = liveBoxRef.current?.wPx ?? Math.max(20, wPx * sx);
    const newHpx = liveBoxRef.current?.hPx ?? Math.max(12, hPx * sy);

    let patch = {
      x_mm: pxToMm(xPx + node.x() - newWpx / 2),
      y_mm: pxToMm(yPx + node.y() - newHpx / 2),
      w_mm: pxToMm(newWpx),
      h_mm: pxToMm(newHpx)
    };

    const snapped = snapResizeBox({
      xMm: patch.x_mm,
      yMm: patch.y_mm,
      wMm: patch.w_mm,
      hMm: patch.h_mm
    });
    patch = {
      x_mm: snapped.xMm,
      y_mm: snapped.yMm,
      w_mm: snapped.wMm,
      h_mm: snapped.hMm
    };

    // Clamp resized text box into the margin guide (content only).
    if (doc === "content") {
      const box = pickMarginBoxMm(marginBoxesMm, pageWmm, patch.x_mm, patch.w_mm);
      if (box) {
        const clamped = clampRectIntoBoxMm(
          { x: patch.x_mm, y: patch.y_mm, w: patch.w_mm, h: patch.h_mm },
          box
        );
        patch = {
          x_mm: clamped.x,
          y_mm: clamped.y,
          w_mm: clamped.w,
          h_mm: clamped.h
        };
      }
    }

    const snappedWpx = mmToPx(patch.w_mm);
    const snappedHpx = mmToPx(patch.h_mm);

    node.scaleX(1);
    node.scaleY(1);
    node.offsetX(snappedWpx / 2);
    node.offsetY(snappedHpx / 2);
    node.x(mmToPx(patch.x_mm) - xPx + snappedWpx / 2);
    node.y(mmToPx(patch.y_mm) - yPx + snappedHpx / 2);

    if (textRef.current) {
      textRef.current.x(0);
      textRef.current.y(0);
      textRef.current.width(snappedWpx);
      textRef.current.height(snappedHpx);
      textRef.current.scaleX(1);
      textRef.current.scaleY(1);
    }

    if (selectionRectRef.current) {
      selectionRectRef.current.x(0);
      selectionRectRef.current.y(0);
      selectionRectRef.current.width(snappedWpx);
      selectionRectRef.current.height(snappedHpx);
      selectionRectRef.current.scaleX(1);
      selectionRectRef.current.scaleY(1);
    }

    if (guideRectRef.current) {
      guideRectRef.current.x(paddingPx);
      guideRectRef.current.y(paddingPx);
      guideRectRef.current.width(Math.max(1, snappedWpx - paddingPx * 2));
      guideRectRef.current.height(Math.max(1, snappedHpx - paddingPx * 2));
      guideRectRef.current.scaleX(1);
      guideRectRef.current.scaleY(1);
    }

    if (hitRectRef.current) {
      hitRectRef.current.x(0);
      hitRectRef.current.y(0);
      hitRectRef.current.width(snappedWpx);
      hitRectRef.current.height(snappedHpx);
      hitRectRef.current.scaleX(1);
      hitRectRef.current.scaleY(1);
    }

    if (clipGroupRef.current) {
      clipGroupRef.current.clipWidth(snappedWpx);
      clipGroupRef.current.clipHeight(snappedHpx);
      clipGroupRef.current.scaleX(1);
      clipGroupRef.current.scaleY(1);
    }

    liveBoxRef.current = null;
    node.getLayer()?.draw();
    store.updateElement(doc, el.id, patch);
  }

  return (
    <Group
      key={key}
      x={xPx}
      y={yPx}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true;
        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        store.selectElement(doc, el.id || null);
        onFocusPage?.(pageIndex);
        onElementSelect?.();
      }}
      onDragMove={(e) => {
        const node = e.target;
        const xMm = pxToMm(node.x());
        const yMm = pxToMm(node.y());
        const wMm = Number(el.w_mm ?? 0);
        const hMm = Number(el.h_mm ?? 0);
        const clampBox = doc === "content"
          ? pickMarginBoxMm(marginBoxesMm, pageWmm, xMm, wMm)
          : null;

        liveSnapMove({
          node,
          xMm,
          yMm,
          wMm,
          hMm,
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2,
          clampBox,
          clampMode: "modelBox"
        });
      }}
      onDragEnd={(e) => {
        const node = e.target;
        const xMm = pxToMm(node.x());
        const yMm = pxToMm(node.y());
        const wMm = Number(el.w_mm ?? 0);
        const hMm = Number(el.h_mm ?? 0);
        const clampBox = doc === "content"
          ? pickMarginBoxMm(marginBoxesMm, pageWmm, xMm, wMm)
          : null;

        const snapped = liveSnapMove({
          node,
          xMm,
          yMm,
          wMm,
          hMm,
          xs: snapTargets.xs,
          ys: snapTargets.ys,
          thresholdMm: 2,
          clampBox,
          clampMode: "modelBox"
        });

        store.updateElement(doc, el.id, {
          x_mm: snapped.xMm,
          y_mm: snapped.yMm
        });
      }}
    >
      <Group
        ref={groupRef}
        id={el.id}
        x={wPx / 2}
        y={hPx / 2}
        offsetX={wPx / 2}
        offsetY={hPx / 2}
        rotation={rotate}
        onDblClick={() => {
          const node = groupRef.current;
          if (!node) return;
          onInlineEdit({
            doc,
            elementId: el.id,
            value: String(el.text || ""),
            node,
            style: el.style || {}
          });
        }}
        onTransform={(e) => {
          const node = e.target;
          const sx = Math.abs(Number(node.scaleX?.() ?? 1));
          const sy = Math.abs(Number(node.scaleY?.() ?? 1));
          const nextWpx = Math.max(20, wPx * sx);
          const nextHpx = Math.max(12, hPx * sy);
          applyLiveTextPreview(node, sx, sy, nextWpx, nextHpx);
        }}
        onTransformEnd={(e) => {
          commitTransform(e.target);
        }}
      >
        {/* Invisible bounds rect defines the textbox box for Transformer and hit-testing. */}
        <Rect ref={hitRectRef} x={0} y={0} width={wPx} height={hPx} opacity={0} listening={false} />
        {isSelected && paddingPx > 0 ? (
          <Rect
            ref={guideRectRef}
            x={paddingPx}
            y={paddingPx}
            width={Math.max(1, wPx - 2 * paddingPx)}
            height={Math.max(1, hPx - 2 * paddingPx)}
            stroke="#64748b"
            opacity={0.55}
            dash={[6, 6]}
            strokeWidth={1}
            strokeScaleEnabled={false}
            listening={false}
          />
        ) : null}

        {/* Text content stays in a horizontal textbox; the group transform provides rotation. */}
        <Group ref={clipGroupRef} clipX={0} clipY={0} clipWidth={wPx} clipHeight={hPx}>
          <Text
            ref={textRef}
            x={0}
            y={0}
            width={wPx}
            height={hPx}
            text={renderTextForPreview(el)}
            wrap="word"
            align={String(el.style?.text_align || "left")}
            lineHeight={lineHeight}
            fontFamily={family}
            fontStyle={fontStyle}
            fontSize={ptToPx(fontSizePt)}
            fill={fill}
            padding={paddingPx}
            textDecoration={underline ? "underline" : ""}
            shadowEnabled={shadowEnabled}
            shadowColor={shadowColor}
            shadowOpacity={shadowOpacity}
            shadowBlur={shadowBlur}
            shadowOffsetX={shadowOffsetX}
            shadowOffsetY={shadowOffsetY}
          />
        </Group>
      </Group>

      {isSelected && !isInlineEditing ? (
        <RotateHandle
          rootRef={groupRef}
          handleRef={rotateHandleWrapRef}
          frameW={wPx}
          frameH={hPx}
          rotateDeg={rotate}
          onRotate={(deg) => store.updateElementTransient(doc, el.id, { rotate_deg: deg })}
          onRotateCommit={(deg) => store.updateElement(doc, el.id, { rotate_deg: deg })}
        />
      ) : null}
    </Group>
  );
}

export default function CanvasEditor({
  doc,
  formats,
  pageIndex,
  onFocusPage,
  onCopySelected,
  onPaste,
  onDuplicate,
  onDropAsset,
  onElementSelect,
  onImagePlaceholderDoubleClick,
  onStageMetrics,
  onSelectionCenter,
  onThumbnail,
  unitId,
  extraElements,
  pageWidthMm,
  artboardWidthMmOverride,
  showGutter,
  gutterXmm,
  fitViewport,
  zoomMode,
  manualZoom,
  readOnly,
  thumbnailOnly,
}: {
  doc: "content" | "cover";
  formats: any[];
  pageIndex: number;
  onFocusPage?: (pageIndex: number) => void;
  onCopySelected?: () => void;
  onPaste?: () => void;
  onDuplicate?: () => void;
  onElementSelect?: () => void;
  onImagePlaceholderDoubleClick?: (payload: { doc: "content" | "cover"; pageIndex: number; elementId: string }) => void;
  onDropAsset?: (payload: {
    asset_id: number;
    image_url?: string;
    width_px?: number;
    height_px?: number;
    x_mm: number;
    y_mm: number;
    targetDoc: "content" | "cover";
    targetUnitIndex: number;
  }) => void;
  onStageMetrics?: (m: { stageW: number; stageH: number }) => void;
  onSelectionCenter?: (p: { x: number; y: number } | null) => void;
  onThumbnail?: (unitId: string, dataUrl: string) => void;
  unitId?: string;
  extraElements?: any[];
  pageWidthMm?: number;
  artboardWidthMmOverride?: number;
  showGutter?: boolean;
  gutterXmm?: number;
  fitViewport?: { w: number; h: number };
  zoomMode?: "fit" | "manual";
  manualZoom?: number;
  readOnly?: boolean;
  thumbnailOnly?: boolean;
}) {
  const store = useEditorStore();
  const stageRef = useRef<any>(null);
  const trRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const snapGuideTimerRef = useRef<number | null>(null);
  const resizeAnchorRafRef = useRef<number | null>(null);
  const resizeAnchorIdleTimerRef = useRef<number | null>(null);

  const docData = doc === "content" ? store.contentData : store.coverData;
  const [snapGuide, setSnapGuide] = useState<{
    xPx?: number;
    yPx?: number;
  } | null>(null);
  const [resizeAnchorTracking, setResizeAnchorTracking] = useState(false);
  const [resizeAnchorPositions, setResizeAnchorPositions] = useState<Array<{ name: string; x: number; y: number }>>([]);
  const [cropSession, setCropSession] = useState<CropSession>(null);
  const [cropModalTarget, setCropModalTarget] = useState<null | { doc: "content" | "cover"; elementId: string }>(null);
  // Track inline text editing state (HTML textarea overlay)
  const [editing, setEditing] = useState<{
    doc: "content" | "cover";
    elementId: string;
    value: string;
    left: number;
    top: number;
    width: number;
    height: number;
    scale: number;
    style?: any;
  } | null>(null);
  const [textToolbar, setTextToolbar] = useState<null | { left: number; top: number }>(null);
  const [imageToolbar, setImageToolbar] = useState<null | { left: number; top: number }>(null);
  const [casewrapGuideClosed, setCasewrapGuideClosed] = useState(false);
  const isThumbnailOnly = Boolean(thumbnailOnly);

  useEffect(() => {
    if (!readOnly && !isThumbnailOnly) return;
    setCropSession(null);
    setCropModalTarget(null);
    setEditing(null);
    setTextToolbar(null);
    setImageToolbar(null);
    trRef.current?.nodes?.([]);
    trRef.current?.getLayer?.()?.batchDraw?.();
  }, [isThumbnailOnly, readOnly]);

  // Ensure ids once when data exists
  useEffect(() => {
    if (!docData) return;
    const fixed = ensureElementIds(docData);
    if (fixed !== docData) {
      if (doc === "content") store.setContentData(fixed);
      else store.setCoverData(fixed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docData, doc]);

  /**
   * Start crop mode for an image while preserving the previous frame state for Cancel.
   */
  const startCrop = useCallback((cropDoc: "content" | "cover", elementId: string, element: any) => {
    if (readOnly) return;
    if (!elementId || !element) return;

    setCropSession({
      doc: cropDoc,
      elementId,
      prev: {
        image_mode: element.image_mode,
        fit: element.fit,
        scale: element.scale,
        pan_mm: element.pan_mm,
        rotate_deg: element.rotate_deg,
      }
    });

    store.updateElementTransient(cropDoc, elementId, {
      image_mode: "frame",
      fit: "cover",
      scale: element.scale ?? 1,
      pan_mm: element.pan_mm || { x: 0, y: 0 },
    });
  }, [readOnly, store]);

  /**
   * Finish crop mode. Geometry is already in store via transient updates.
   */
  const doneCrop = useCallback(() => {
    if (!cropSession) return;

    const { doc: cropDoc, elementId } = cropSession;
    const currentDoc = cropDoc === "content" ? store.contentData : store.coverData;
    const found = findElementWithPageIndex(currentDoc, elementId);
    const element = found?.el;

    if (element) {
      store.updateElement(cropDoc, elementId, {
        image_mode: "frame",
        fit: "cover",
        scale: Number(element.scale ?? 1),
        pan_mm: element.pan_mm || { x: 0, y: 0 },
      });
    }

    setCropSession(null);
  }, [cropSession, store]);

  /**
   * Cancel crop by restoring the image state captured on crop start.
   */
  const cancelCrop = useCallback(() => {
    if (!cropSession) return;
    store.updateElementTransient(cropSession.doc, cropSession.elementId, cropSession.prev);
    setCropSession(null);
  }, [cropSession, store]);

  const formatKey = doc === "content" ? docData?.meta?.format_key : docData?.cover_meta?.format_key;
  const fmt = useMemo(() => formats.find((f) => f.id === formatKey) || formats[0], [formats, formatKey]);

  const pageWmm = Number(pageWidthMm ?? fmt?.page_mm?.width ?? 210);
  const pageHmm = fmt?.page_mm?.height || 297;

  // Determine artboard size (mm) for current doc/page
  let artWmm = pageWmm;
  let artHmm = pageHmm;

  // Bleed/safe values from doc meta (fallback to defaults)
  const bleedMm =
    doc === "content"
      ? Number(docData?.meta?.bleed_mm ?? 3)
      : Number(docData?.cover_meta?.bleed_mm ?? 3);

  const safeMm =
    doc === "content"
      ? Number(docData?.meta?.safe_mm ?? 5)
      : Number(docData?.cover_meta?.safe_mm ?? 5);
  const pageMarginsMm =
    doc === "content"
      ? normalizePageMarginsMm(docData?.meta?.page_margins_mm)
      : null;

  // Cover spread: show back + spine + front as a single canvas
  const coverMode = doc === "cover" ? String(docData?.cover_meta?.cover_mode || "spread") : "n/a";
  const spineMm = doc === "cover" ? Number(docData?.cover_meta?.spine_mm ?? 18) : 0;
  const coverSpreadGeometry = doc === "cover"
    ? resolveCoverSpreadGeometry({
        panelWmm: pageWmm,
        panelHmm: pageHmm,
        spineMm,
        coverGeometryType: docData?.cover_meta?.cover_geometry_type,
        coverWrapMm: docData?.cover_meta?.cover_wrap_mm,
        coverSqueezeMm: docData?.cover_meta?.cover_squeeze_mm,
        coverOverlapMm: docData?.cover_meta?.cover_overlap_mm,
      })
    : null;
  const contentPages = doc === "content" && Array.isArray(docData?.pages) ? docData.pages : [];
  const contentLayout =
    doc === "content" ? String(docData?.meta?.content_layout || "book") : "n/a";
  const isSpreadUnit =
    doc === "content"
      ? String(contentPages[pageIndex]?.kind || "single") === "spread"
      : false;
  const isBookMiddleSpread =
    doc === "content" &&
    contentLayout === "book" &&
    isSpreadUnit &&
    pageIndex > 0 &&
    pageIndex < contentPages.length - 1;
  const isSpreadsOnlySpread =
    doc === "content" &&
    contentLayout === "spreads_only" &&
    isSpreadUnit;

  if (doc === "cover" && coverMode === "spread") {
    artWmm = coverSpreadGeometry?.artWmm || (pageWmm * 2 + spineMm);
    artHmm = coverSpreadGeometry?.artHmm || pageHmm;
  }
  const showCasewrapGuide =
    !isThumbnailOnly &&
    doc === "cover" &&
    coverMode === "spread" &&
    coverSpreadGeometry?.type === "casewrap" &&
    !casewrapGuideClosed;

  if (doc === "content" && Number(artboardWidthMmOverride ?? 0) > 0) {
    artWmm = Number(artboardWidthMmOverride);
    artHmm = pageHmm;
  }

  const artWpx = mmToPx(artWmm);
  const artHpx = mmToPx(artHmm);

  const bleedPx = mmToPx(bleedMm);
  const safePx = mmToPx(safeMm);
  const bleedGuideInsetPx = 1;
  const gutterXPx = mmToPx(Number(gutterXmm ?? 0));
  const showContentGutter = (isBookMiddleSpread || isSpreadsOnlySpread) && Boolean(showGutter);
  const showGutterShadow = showContentGutter;
  const isWideSpreadCanvas = doc === "content" && isSpreadUnit;
  const marginBoxes = useMemo(
    () =>
      doc === "content" && pageMarginsMm
        ? getMarginBoxesMm({
            trimWmm: pageWmm,
            trimHmm: pageHmm,
            margins: pageMarginsMm,
            isSpread: isSpreadUnit
          })
        : [],
    [doc, isSpreadUnit, pageHmm, pageMarginsMm, pageWmm]
  );
  const margin = bleedPx;
  const stageW = artWpx + 2 * margin;
  const stageH = artHpx + 2 * margin;
  const fitPaddingX = isWideSpreadCanvas ? 0 : 20;
  const fitPaddingY = 0;

  let fitZoom = 1;
  if (fitViewport?.w && fitViewport?.h) {
    const availW = Math.max(100, fitViewport.w - 2 * fitPaddingX);
    const availH = Math.max(100, fitViewport.h - 2 * fitPaddingY);
    fitZoom = Math.min(availW / stageW, availH / stageH);
    fitZoom = Math.max(0.1, Math.min(4, fitZoom));
  }

  const finalZoom = zoomMode === "manual" ? (manualZoom ?? 1) : fitZoom;
  const viewScale = fitViewport ? finalZoom : 1;

  useEffect(() => {
    onStageMetrics?.({ stageW, stageH });
  }, [onStageMetrics, stageH, stageW]);

  useEffect(() => {
    if (!onDropAsset || isThumbnailOnly) return;

    const onAssetPointerDrop = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const assetId = Number(detail.asset_id || 0);
      const clientX = Number(detail.client_x);
      const clientY = Number(detail.client_y);
      if (!assetId || !wrapperRef.current || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return;

      const widthPxRaw = Number(detail.width_px);
      const heightPxRaw = Number(detail.height_px);
      const localX = (clientX - rect.left) / viewScale - margin;
      const localY = (clientY - rect.top) / viewScale - margin;

      onDropAsset({
        asset_id: assetId,
        width_px: Number.isFinite(widthPxRaw) && widthPxRaw > 0 ? widthPxRaw : undefined,
        height_px: Number.isFinite(heightPxRaw) && heightPxRaw > 0 ? heightPxRaw : undefined,
        x_mm: pxToMm(localX),
        y_mm: pxToMm(localY),
        targetDoc: doc,
        targetUnitIndex: pageIndex,
      });
    };

    window.addEventListener("editor:asset-pointer-drop", onAssetPointerDrop as EventListener);
    return () => window.removeEventListener("editor:asset-pointer-drop", onAssetPointerDrop as EventListener);
  }, [doc, isThumbnailOnly, margin, onDropAsset, pageIndex, viewScale]);

  // Render the requested page only. Book-like spreads are composed outside.
  const elements = useMemo(() => {
    const pages = Array.isArray(docData?.pages) ? docData.pages : [];
    const p = pages[pageIndex] || { elements: [] };
    return Array.isArray(p.elements) ? p.elements : [];
  }, [docData, pageIndex]);
  const all = useMemo(() => [...(extraElements || []), ...elements], [elements, extraElements]);
  const ordered = useMemo(() => {
    return all
      .map((element: any, index: number) => ({ element, index }))
      .sort((a, b) => {
        const aBackground = a.element?.is_background ? -1 : 0;
        const bBackground = b.element?.is_background ? -1 : 0;
        if (aBackground !== bBackground) return aBackground - bBackground;
        return a.index - b.index;
      })
      .map((entry) => entry.element);
  }, [all]);
  const orderSignature = useMemo(
    () =>
      ordered
        .map((element: any, index: number) => String(element?._ghost_key || element?.id || `${element?.type || "el"}_${index}`))
        .join("|"),
    [ordered]
  );
  const snapTargets = useMemo(
    () =>
      buildSnapTargetsMm({
        artWmm,
        artHmm,
        bleedMm,
        safeMm,
        showGutter: Boolean(showGutter),
        gutterXmm: Number(gutterXmm || 0)
      }),
    [artHmm, artWmm, bleedMm, gutterXmm, safeMm, showGutter]
  );

  const flashSnapGuide = useCallback((guide: { xPx?: number; yPx?: number }) => {
    setSnapGuide(guide);
    if (snapGuideTimerRef.current != null) {
      window.clearTimeout(snapGuideTimerRef.current);
    }
    snapGuideTimerRef.current = window.setTimeout(() => setSnapGuide(null), 250);
  }, []);

  const flashResizeGuide = useCallback((snapped: { xGuideMm: number | null; yGuideMm: number | null }) => {
    const guide: { xPx?: number; yPx?: number } = {};
    if (snapped.xGuideMm != null) guide.xPx = mmToPx(snapped.xGuideMm);
    if (snapped.yGuideMm != null) guide.yPx = mmToPx(snapped.yGuideMm);
    if (guide.xPx != null || guide.yPx != null) {
      flashSnapGuide(guide);
    }
  }, [flashSnapGuide]);

  const liveSnapMove = useCallback(({
    node,
    xMm,
    yMm,
    wMm,
    hMm,
    xs,
    ys,
    thresholdMm,
    clampBox,
    clampMode
  }: {
    node: any;
    xMm: number;
    yMm: number;
    wMm: number;
    hMm: number;
    xs: number[];
    ys: number[];
    thresholdMm: number;
    clampBox?: { x: number; y: number; w: number; h: number } | null;
    clampMode?: "clientRect" | "modelBox";
  }) => {
    const snapped = snapElementPositionMmWithGuide({ xMm, yMm, wMm, hMm, xs, ys, thresholdMm });

    node.x(mmToPx(snapped.xMm));
    node.y(mmToPx(snapped.yMm));

    if (clampBox) {
      if (clampMode === "modelBox") {
        const maxX = clampBox.x + Math.max(0, clampBox.w - wMm);
        const maxY = clampBox.y + Math.max(0, clampBox.h - hMm);
        const clampedX = clamp(snapped.xMm, clampBox.x, maxX);
        const clampedY = clamp(snapped.yMm, clampBox.y, maxY);

        if (clampedX !== snapped.xMm || clampedY !== snapped.yMm) {
          snapped.xMm = clampedX;
          snapped.yMm = clampedY;
          node.x(mmToPx(snapped.xMm));
          node.y(mmToPx(snapped.yMm));
        }
      } else {
        // Clamp based on the node's real bounding box so rotation/offset/font metrics
        // cannot visually push the element outside the allowed area.
        const layer = node.getLayer?.();
        if (layer) {
          const r = node.getClientRect({ relativeTo: layer });
          const rectXmm = pxToMm(r.x);
          const rectYmm = pxToMm(r.y);
          const rectWmm = pxToMm(r.width);
          const rectHmm = pxToMm(r.height);

          const maxX = clampBox.x + Math.max(0, clampBox.w - rectWmm);
          const maxY = clampBox.y + Math.max(0, clampBox.h - rectHmm);

          const clampedRectX = clamp(rectXmm, clampBox.x, maxX);
          const clampedRectY = clamp(rectYmm, clampBox.y, maxY);

          const dxMm = clampedRectX - rectXmm;
          const dyMm = clampedRectY - rectYmm;

          if (dxMm !== 0 || dyMm !== 0) {
            node.x(node.x() + mmToPx(dxMm));
            node.y(node.y() + mmToPx(dyMm));
          }

          snapped.xMm = pxToMm(node.x());
          snapped.yMm = pxToMm(node.y());
        }
      }
    }

    const guide: { xPx?: number; yPx?: number } = {};
    if (snapped.xTargetMm != null) guide.xPx = mmToPx(snapped.xTargetMm);
    if (snapped.yTargetMm != null) guide.yPx = mmToPx(snapped.yTargetMm);
    if (guide.xPx != null || guide.yPx != null) {
      flashSnapGuide(guide);
    }

    scheduleResizeAnchorPositions();
    node.getLayer()?.batchDraw();
    return snapped;
  }, [flashSnapGuide]);

  const snapResizeBox = useCallback(({
    xMm,
    yMm,
    wMm,
    hMm
  }: {
    xMm: number;
    yMm: number;
    wMm: number;
    hMm: number;
  }) => {
    const snapped = snapResizeBoxMmWithGuide({
      xMm,
      yMm,
      wMm,
      hMm,
      xs: snapTargets.xs,
      ys: snapTargets.ys,
      thresholdMm: 2,
      minWmm: 5,
      minHmm: 5
    });

    flashResizeGuide(snapped);
    return snapped;
  }, [flashResizeGuide, snapTargets.xs, snapTargets.ys]);

  const selectedId = !isThumbnailOnly && store.selected.doc === doc ? store.selected.elementId : null;
  const selectedType = !isThumbnailOnly ? findSelectedType(docData, selectedId) : null;
  const selectedElement = useMemo(
    () => (selectedId ? findElementWithPageIndex(docData, selectedId)?.el || null : null),
    [docData, selectedId]
  );
  const showResizeArrows =
    !isThumbnailOnly && (
      selectedType === "image" ||
      selectedType === "text" ||
      selectedType === "rect" ||
      selectedType === "shape" ||
      selectedType === "background"
  );
  // Keep refs to Konva nodes by element id
  const nodeRefs = useRef<Map<string, any>>(new Map());
  const [nodeRegistryVersion, setNodeRegistryVersion] = useState(0);
  const layerRef = useRef<any>(null);
  const paperRef = useRef<any>(null);
  // Register Konva node ref for Transformer selection
  const registerNode = useCallback((id: string, node: any) => {
    if (node) nodeRefs.current.set(id, node);
    else nodeRefs.current.delete(id);
    // English comment: Free images register their target only after the image loads.
    // Bump a lightweight version so the Transformer can attach to late-registered nodes.
    setNodeRegistryVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    return () => {
      if (snapGuideTimerRef.current != null) {
        window.clearTimeout(snapGuideTimerRef.current);
      }
      if (resizeAnchorRafRef.current != null) {
        window.cancelAnimationFrame(resizeAnchorRafRef.current);
      }
      if (resizeAnchorIdleTimerRef.current != null) {
        window.clearTimeout(resizeAnchorIdleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;

    if (readOnly) {
      tr.nodes([]);
      clearResizeAnchorTracking();
      tr.getLayer()?.batchDraw();
      return;
    }

    // Hide transformer while cropping.
    if (cropSession) {
      tr.nodes([]);
      clearResizeAnchorTracking();
      tr.getLayer()?.batchDraw();
      return;
    }

    if (!selectedId) {
      tr.nodes([]);
      clearResizeAnchorTracking();
      tr.getLayer()?.batchDraw();
      return;
    }

    const node = nodeRefs.current.get(selectedId);
    if (!node) {
      tr.nodes([]);
      clearResizeAnchorTracking();
      tr.getLayer()?.batchDraw();
      return;
    }

    tr.nodes([node]);
    tr.getLayer()?.batchDraw();
    scheduleResizeAnchorPositions();
  }, [cropSession, nodeRegistryVersion, orderSignature, pageIndex, readOnly, selectedId]);

  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;

    const onStart = () => {
      const node = tr.nodes?.()?.[0];
      const freeImage = Boolean(node?.getAttr?.("imageModeFree"));
      const anchor = tr.getActiveAnchor?.();
      tr.keepRatio(Boolean(freeImage && isCornerAnchor(anchor)));
      setResizeAnchorTracking(true);
      scheduleResizeAnchorPositions();
    };

    const onEnd = () => {
      tr.keepRatio(false);
      tr.forceUpdate?.();
      refreshResizeAnchorPositions();
      clearResizeAnchorTrackingSoon();
    };

    const onTransform = () => {
      scheduleResizeAnchorPositions();
    };

    tr.on("transformstart", onStart);
    tr.on("transform", onTransform);
    tr.on("transformend", onEnd);

    return () => {
      tr.off("transformstart", onStart);
      tr.off("transform", onTransform);
      tr.off("transformend", onEnd);
    };
  }, []);

  useEffect(() => {
    const tr = trRef.current;
    const layer = layerRef.current;

    if (!tr || !layer) {
      onSelectionCenter?.(null);
      return;
    }

    const nodes = tr.nodes?.();
    if (!nodes || nodes.length !== 1) {
      onSelectionCenter?.(null);
      return;
    }

    const node = nodes[0];
    const r = node.getClientRect();
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;

    onSelectionCenter?.({ x: cx, y: cy });
  }, [docData, onSelectionCenter, pageIndex, selectedId]);

  useEffect(() => {
    if (!selectedId || selectedType !== "text" || editing) {
      setTextToolbar(null);
      return;
    }
    const currentId = selectedId;

    function updateToolbar() {
      const node = nodeRefs.current.get(currentId);
      const stage = stageRef.current;
      if (!node || !stage) {
        setTextToolbar(null);
        return;
      }

      const TOOLBAR_GAP = 8;
      const TOOLBAR_W = 260;
      const TOOLBAR_H = 36;
      const rect = node.getClientRect();
      const containerRect = stage.container().getBoundingClientRect();
      const left = containerRect.left + rect.x;
      let top = containerRect.top + rect.y + rect.height + TOOLBAR_GAP;

      if (top + TOOLBAR_H > window.innerHeight - 8) {
        top = containerRect.top + rect.y - TOOLBAR_H - TOOLBAR_GAP;
      }

      const clampedLeft = Math.max(8, Math.min(window.innerWidth - TOOLBAR_W - 8, left));
      const clampedTop = Math.max(8, Math.min(window.innerHeight - TOOLBAR_H - 8, top));

      setTextToolbar({
        left: clampedLeft,
        top: clampedTop
      });
    }

    updateToolbar();
    window.addEventListener("resize", updateToolbar);
    window.addEventListener("scroll", updateToolbar, true);

    return () => {
      window.removeEventListener("resize", updateToolbar);
      window.removeEventListener("scroll", updateToolbar, true);
    };
  }, [selectedId, selectedType, editing, docData, pageIndex, orderSignature, viewScale]);

  useEffect(() => {
    if (!selectedId || selectedType !== "image") {
      setImageToolbar(null);
      return;
    }
    const element = selectedElement;
    if (!element) {
      setImageToolbar(null);
      return;
    }

    function updateToolbar() {
      const stage = stageRef.current;
      if (!stage) {
        setImageToolbar(null);
        return;
      }

      const containerRect = stage.container().getBoundingClientRect();
      const x = mmToPx(Number(element.x_mm || 0));
      const y = mmToPx(Number(element.y_mm || 0));
      const w = mmToPx(Math.max(1, Number(element.w_mm || 1)));
      const h = mmToPx(Math.max(1, Number(element.h_mm || 1)));
      const rotate = (Number(element.rotate_deg || 0) * Math.PI) / 180;
      const cx = x + w / 2;
      const cy = y + h / 2;
      const cos = Math.cos(rotate);
      const sin = Math.sin(rotate);
      const points = [
        { x, y },
        { x: x + w, y },
        { x: x + w, y: y + h },
        { x, y: y + h },
      ].map((point) => {
        const dx = point.x - cx;
        const dy = point.y - cy;
        return {
          x: cx + dx * cos - dy * sin,
          y: cy + dx * sin + dy * cos,
        };
      });
      const minX = Math.min(...points.map((point) => point.x));
      const maxY = Math.max(...points.map((point) => point.y));
      const TOOLBAR_GAP = 10;
      const TOOLBAR_W = 300;
      const TOOLBAR_H = 44;
      const rawLeft = containerRect.left + (margin + minX) * viewScale;
      const rawTop = containerRect.top + (margin + maxY) * viewScale + TOOLBAR_GAP;
      const top =
        rawTop + TOOLBAR_H > window.innerHeight - 8
          ? containerRect.top + (margin + Math.min(...points.map((point) => point.y))) * viewScale - TOOLBAR_H - TOOLBAR_GAP
          : rawTop;

      setImageToolbar({
        left: Math.max(8, Math.min(window.innerWidth - TOOLBAR_W - 8, rawLeft)),
        top: Math.max(8, Math.min(window.innerHeight - TOOLBAR_H - 8, top))
      });
    }

    updateToolbar();
    window.addEventListener("resize", updateToolbar);
    window.addEventListener("scroll", updateToolbar, true);

    return () => {
      window.removeEventListener("resize", updateToolbar);
      window.removeEventListener("scroll", updateToolbar, true);
    };
  }, [selectedId, selectedType, selectedElement, docData, pageIndex, orderSignature, viewScale, margin]);

  useEffect(() => {
    // Enable keyboard shortcuts for text toolbar when a text element is selected
    // and inline editing is NOT active.
    if (!textToolbar) return;
    if (editing) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTypingTarget =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        Boolean(target?.isContentEditable);

      if (isTypingTarget) return;

      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;

      const key = e.key.toLowerCase();

      if (key === "c") {
        e.preventDefault();
        e.stopPropagation();
        onCopySelected?.();
        return;
      }

      if (key === "v") {
        e.preventDefault();
        e.stopPropagation();
        onPaste?.();
        return;
      }

      if (key === "d") {
        e.preventDefault();
        e.stopPropagation();
        onDuplicate?.();
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [textToolbar, editing, onCopySelected, onPaste, onDuplicate]);

  const selectedImage = selectedType === "image" ? selectedElement : null;
  const cropModalElement = useMemo(() => {
    if (!cropModalTarget) return null;
    const targetDocData = cropModalTarget.doc === "content" ? store.contentData : store.coverData;
    return findElementWithPageIndex(targetDocData, cropModalTarget.elementId)?.el || null;
  }, [cropModalTarget, store.contentData, store.coverData]);
  const imageCropMode = Boolean(
    cropSession &&
    cropSession.doc === doc &&
    cropSession.elementId &&
    cropSession.elementId === selectedId
  );

  const handleImageZoomOut = useCallback(() => {
    if (!selectedImage || !selectedId) return;

    if (String(selectedImage.image_mode || "frame") === "free") {
      const w = Number(selectedImage.w_mm ?? 80);
      const h = Number(selectedImage.h_mm ?? 60);
      store.updateElement(doc, selectedId, { w_mm: w / IMAGE_ZOOM_FACTOR, h_mm: h / IMAGE_ZOOM_FACTOR });
      return;
    }

    store.updateElement(doc, selectedId, {
      scale: Math.max(0.25, Number(selectedImage.scale ?? 1) - 0.1)
    });
  }, [doc, selectedId, selectedImage, store]);

  const handleImageZoomIn = useCallback(() => {
    if (!selectedImage || !selectedId) return;

    if (String(selectedImage.image_mode || "frame") === "free") {
      const w = Number(selectedImage.w_mm ?? 80);
      const h = Number(selectedImage.h_mm ?? 60);
      store.updateElement(doc, selectedId, { w_mm: w * IMAGE_ZOOM_FACTOR, h_mm: h * IMAGE_ZOOM_FACTOR });
      return;
    }

    store.updateElement(doc, selectedId, {
      scale: Math.min(5, Number(selectedImage.scale ?? 1) + 0.1)
    });
  }, [doc, selectedId, selectedImage, store]);

  const handleImageCrop = useCallback(() => {
    if (!selectedImage || !selectedId) return;
    const imageUrl =
      String(selectedImage.image_url || "") ||
      (selectedImage.source === "clipart" && selectedImage.clipart_id
        ? getClipartPublicUrl(String(selectedImage.clipart_id))
        : "");
    if (!imageUrl) return;
    setCropModalTarget({ doc, elementId: selectedId });
  }, [doc, selectedId, selectedImage]);

  const handleImageRemove = useCallback(() => {
    if (!selectedId) return;
    store.deleteElement(doc, selectedId);
  }, [doc, selectedId, store]);

  /**
   * Generate a lightweight thumbnail for the current unit/page view.
   * MVP: captures the current Konva stage with visible guides and overlays.
   */
  useEffect(() => {
    if (!onThumbnail || !unitId) return;

    const layer = layerRef.current;
    if (!layer) return;
    let cancelled = false;

    const t = window.setTimeout(() => {
      (async () => {
        try {
          await preloadCanvasImageUrls(getElementImageUrls(elements));
          if (cancelled) return;
          await new Promise((resolve) => window.requestAnimationFrame(resolve));
          if (cancelled) return;
          const url = makeThumbnailPng({
            layer,
            artWpx,
            artHpx,
            pixelRatio: 0.18
          });
          onThumbnail(unitId, url);
        } catch {
          // Ignore thumbnail generation failures in MVP mode.
        }
      })();
    }, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [artHpx, artWpx, doc, pageIndex, unitId, onThumbnail, elements, selectedId, snapGuide]);

  /**
   * Start inline editing by placing an HTML textarea on top of the Konva node rect.
   * Node rect is taken in screen-space stage coordinates (already includes stage scale).
   */
  function startInlineEdit(args: {
    doc: "content" | "cover";
    elementId: string;
    value: string;
    node: any;
    style?: any;
  }) {
    if (readOnly) return;
    const stage = stageRef.current;
    if (!stage || !args.node) return;

    // getClientRect() returns rect in stage coordinate space, already reflecting current stage scale (zoom).
    const containerRect = stage.container().getBoundingClientRect();
    const rect = args.node.getClientRect();
    const left = containerRect.left + rect.x;
    const top = containerRect.top + rect.y;
    const scale = stage.scaleX() || 1;

    setEditing({
      doc: args.doc,
      elementId: args.elementId,
      value: args.value,
      left,
      top,
      width: rect.width,
      height: rect.height,
      scale,
      style: args.style || {}
    });
  }

  function getAnchorPositions() {
    const tr = trRef.current;
    const layer = layerRef.current;
    if (!tr || !layer) return null;

    const out: Array<{ name: string; x: number; y: number }> = [];

    Object.keys(ANCHOR_ICON).forEach((name) => {
      const anchor = tr.findOne?.(`.${name}`);
      if (!anchor) return;

      const abs = anchor.getAbsolutePosition?.();
      if (!abs) return;

      const pt = layer.getAbsoluteTransform().copy().invert().point(abs);
      out.push({ name, x: pt.x, y: pt.y });
    });

    return out;
  }

  function setResizeAnchorPositionsIfChanged(next: Array<{ name: string; x: number; y: number }>) {
    setResizeAnchorPositions((prev) => {
      if (
        prev.length === next.length &&
        prev.every((item, index) => {
          const other = next[index];
          return other?.name === item.name && Math.abs(other.x - item.x) < 0.5 && Math.abs(other.y - item.y) < 0.5;
        })
      ) {
        return prev;
      }
      return next;
    });
  }

  function refreshResizeAnchorPositions() {
    trRef.current?.forceUpdate?.();
    setResizeAnchorPositionsIfChanged(getAnchorPositions() || []);
  }

  function clearResizeAnchorTracking() {
    if (resizeAnchorRafRef.current != null) {
      window.cancelAnimationFrame(resizeAnchorRafRef.current);
      resizeAnchorRafRef.current = null;
    }
    if (resizeAnchorIdleTimerRef.current != null) {
      window.clearTimeout(resizeAnchorIdleTimerRef.current);
      resizeAnchorIdleTimerRef.current = null;
    }
    trRef.current?.forceUpdate?.();
    trRef.current?.getLayer?.()?.batchDraw?.();
    setResizeAnchorTracking(false);
    setResizeAnchorPositions([]);
  }

  function clearResizeAnchorTrackingSoon(delayMs = 80) {
    if (resizeAnchorIdleTimerRef.current != null) {
      window.clearTimeout(resizeAnchorIdleTimerRef.current);
    }
    resizeAnchorIdleTimerRef.current = window.setTimeout(() => {
      resizeAnchorIdleTimerRef.current = null;
      trRef.current?.forceUpdate?.();
      trRef.current?.getLayer?.()?.batchDraw?.();
      setResizeAnchorTracking(false);
      setResizeAnchorPositions([]);
    }, delayMs);
  }

  function scheduleResizeAnchorPositions() {
    setResizeAnchorTracking(true);
    clearResizeAnchorTrackingSoon();
    if (resizeAnchorRafRef.current != null) return;
    resizeAnchorRafRef.current = window.requestAnimationFrame(() => {
      resizeAnchorRafRef.current = null;
      refreshResizeAnchorPositions();
    });
  }

  return (
    <div className="px-3">
      <div>
        <div
          ref={wrapperRef}
          style={{ position: "relative", display: "inline-block" }}
          onDragOver={(e) => {
            if (!onDropAsset) return;
            e.preventDefault();
          }}
          onDrop={(e) => {
            if (!onDropAsset) return;
            e.preventDefault();

            const assetId = Number(e.dataTransfer.getData("text/asset-id"));
            if (!assetId || !wrapperRef.current) return;

            const imageUrl = e.dataTransfer.getData("text/asset-url") || undefined;
            const widthPxRaw = Number(e.dataTransfer.getData("text/asset-w"));
            const heightPxRaw = Number(e.dataTransfer.getData("text/asset-h"));
            const rect = wrapperRef.current.getBoundingClientRect();
            const localX = (e.clientX - rect.left) / viewScale - margin;
            const localY = (e.clientY - rect.top) / viewScale - margin;

            onDropAsset({
              asset_id: assetId,
              image_url: imageUrl,
              width_px: Number.isFinite(widthPxRaw) && widthPxRaw > 0 ? widthPxRaw : undefined,
              height_px: Number.isFinite(heightPxRaw) && heightPxRaw > 0 ? heightPxRaw : undefined,
              x_mm: pxToMm(localX),
              y_mm: pxToMm(localY),
              targetDoc: doc,
              targetUnitIndex: pageIndex,
            });
          }}
        >
          <Stage
            ref={stageRef}
            width={stageW * viewScale}
            height={stageH * viewScale}
            scaleX={viewScale}
            scaleY={viewScale}
            onWheel={(e) => {
              if (!cropSession || cropSession.doc !== doc) return;
              e.evt.preventDefault();

              const stage = stageRef.current;
              if (!stage) return;

              const currentDoc = cropSession.doc === "content" ? store.contentData : store.coverData;
              const found = findElementWithPageIndex(currentDoc, cropSession.elementId);
              if (!found?.el || found.el.type !== "image") return;

              const root = stage.findOne(`#${cropSession.elementId}`);
              const imgNode = stage.findOne(`#${cropSession.elementId}__img`);
              if (!root || !imgNode) return;

              const curScale = Number(found.el.scale ?? 1);
              const step = e.evt.deltaY > 0 ? -0.08 : 0.08;
              const nextScale = Math.max(0.25, Math.min(5, curScale + step));
              if (nextScale === curScale) return;

              const p = stage.getPointerPosition();
              if (!p) return;

              // Pointer must be converted to the same local coordinate space as the cropped image.
              const localRoot = imgNode.getParent?.() || root;
              const inv = localRoot.getAbsoluteTransform().copy().invert();
              const local = inv.point(p);
              const fx = local.x;
              const fy = local.y;

              const frameW = mmToPx(Number(found.el.w_mm ?? 0));
              const frameH = mmToPx(Number(found.el.h_mm ?? 0));
              if (frameW <= 0 || frameH <= 0) return;

              const curImgW = Number(imgNode.width?.() ?? 0);
              const curImgH = Number(imgNode.height?.() ?? 0);
              const curCenterX = Number(imgNode.x?.() ?? frameW / 2);
              const curCenterY = Number(imgNode.y?.() ?? frameH / 2);
              if (curImgW <= 0 || curImgH <= 0) return;

              const ax = (fx - curCenterX) / curImgW;
              const ay = (fy - curCenterY) / curImgH;

              const ratio = nextScale / curScale;
              const nextImgW = curImgW * ratio;
              const nextImgH = curImgH * ratio;
              const nextCenterX = fx - ax * nextImgW;
              const nextCenterY = fy - ay * nextImgH;

              let panXPx = nextCenterX - frameW / 2;
              let panYPx = nextCenterY - frameH / 2;

              const maxX = Math.max(0, (nextImgW - frameW) / 2);
              const maxY = Math.max(0, (nextImgH - frameH) / 2);
              panXPx = Math.max(-maxX, Math.min(maxX, panXPx));
              panYPx = Math.max(-maxY, Math.min(maxY, panYPx));

              imgNode.width(nextImgW);
              imgNode.height(nextImgH);
              imgNode.offsetX(nextImgW / 2);
              imgNode.offsetY(nextImgH / 2);
              imgNode.x(frameW / 2 + panXPx);
              imgNode.y(frameH / 2 + panYPx);
              imgNode.getLayer()?.batchDraw();

              store.updateElementTransient(cropSession.doc, cropSession.elementId, {
                scale: nextScale,
                pan_mm: { x: pxToMm(panXPx), y: pxToMm(panYPx) }
              });
            }}
          >
            <Layer ref={layerRef} x={margin} y={margin}>
              {/* English comment: The printable sheet includes bleed, so the visible paper extends to the bleed guide. */}
              <Rect
                name="bleed-paper-rect"
                x={-bleedPx}
                y={-bleedPx}
                width={artWpx + 2 * bleedPx}
                height={artHpx + 2 * bleedPx}
                fill="#ffffff"
                stroke="#e5e7eb"
                strokeWidth={1}
                shadowBlur={12}
                shadowOpacity={0.18}
                shadowOffsetX={0}
                shadowOffsetY={6}
                onMouseDown={(e) => {
                  e.cancelBubble = true;
                  onFocusPage?.(pageIndex);
                }}
              />
              {/* Artboard (trim) */}
              <Rect
                ref={paperRef}
                name="paper-rect"
                x={0}
                y={0}
                width={artWpx}
                height={artHpx}
                fill="#ffffff"
                stroke="#e5e7eb"
                strokeWidth={1}
                onMouseDown={(e) => {
                  // English comment: Keep selection when clicking on the paper/background.
                  // Properties panel should close only via X button, not by background click.
                  e.cancelBubble = true;
                  onFocusPage?.(pageIndex);
                }}
              />
              <Group
                key={`elements:${doc}:${pageIndex}:${orderSignature}`}
                clipX={-bleedPx}
                clipY={-bleedPx}
                clipWidth={artWpx + 2 * bleedPx}
                clipHeight={artHpx + 2 * bleedPx}
              >
                {ordered.map((el: any, idx: number) => {
                  const key = el?._ghost_key || el?.id || `${el?.type || "el"}_${idx}`;
                  const x = mmToPx(Number(el.x_mm ?? 0));
                  const y = mmToPx(Number(el.y_mm ?? 0));
                  const w = mmToPx(Number(el.w_mm ?? 0));
                  const h = mmToPx(Number(el.h_mm ?? 0));

                  const isSelected = Boolean(!el?._ghost && el.id && el.id === selectedId);

                  if (el.type === "rect" || el.type === "background") {
                    return (
                      <RectElement
                        key={key}
                        doc={doc}
                        el={el}
                        idx={idx}
                        pageIndex={pageIndex}
                        snapTargets={snapTargets}
                        liveSnapMove={liveSnapMove}
                        snapResizeBox={snapResizeBox}
                        marginBoxesMm={marginBoxes}
                        pageWmm={pageWmm}
                        xPx={x}
                        yPx={y}
                        wPx={w}
                        hPx={h}
                        isSelected={isSelected}
                        registerNode={registerNode}
                        onFocusPage={onFocusPage}
                        onElementSelect={onElementSelect}
                      />
                    );
                  }

                  if (el.type === "shape") {
                    return (
                      <ShapeElement
                        key={key}
                        doc={doc}
                        el={el}
                        idx={idx}
                        pageIndex={pageIndex}
                        snapTargets={snapTargets}
                        liveSnapMove={liveSnapMove}
                        snapResizeBox={snapResizeBox}
                        xPx={x}
                        yPx={y}
                        wPx={w}
                        hPx={h}
                        isSelected={isSelected}
                        registerNode={registerNode}
                        onFocusPage={onFocusPage}
                        onElementSelect={onElementSelect}
                      />
                    );
                  }

                  if (el.type === "text") {
                    return (
                      <TextElement
                        key={key}
                        doc={doc}
                        el={el}
                        idx={idx}
                        pageIndex={pageIndex}
                        snapTargets={snapTargets}
                        liveSnapMove={liveSnapMove}
                        snapResizeBox={snapResizeBox}
                        marginBoxesMm={marginBoxes}
                        pageWmm={pageWmm}
                        xPx={x}
                        yPx={y}
                        wPx={w}
                        hPx={h}
                        isSelected={isSelected}
                        isInlineEditing={Boolean(editing && editing.doc === doc && editing.elementId === el.id)}
                        registerNode={registerNode}
                        onInlineEdit={startInlineEdit}
                        onFocusPage={onFocusPage}
                        onElementSelect={onElementSelect}
                        transformerRef={trRef}
                      />
                    );
                  }

                  if (el.type === "image") {
                    return (
                      <ImageElement
                        key={key}
                        doc={doc}
                        el={el}
                        idx={idx}
                        pageIndex={pageIndex}
                        pageWidthMm={pageWidthMm ?? pageWmm}
                        snapTargets={snapTargets}
                        liveSnapMove={liveSnapMove}
                        snapResizeBox={snapResizeBox}
                        xPx={x}
                        yPx={y}
                        wPx={w}
                        hPx={h}
                      isSelected={isSelected}
                      registerNode={registerNode}
                      cropSession={cropSession}
                      startCrop={startCrop}
                      doneCrop={doneCrop}
                      cancelCrop={cancelCrop}
                      onFocusPage={onFocusPage}
                      onElementSelect={onElementSelect}
                      onImagePlaceholderDoubleClick={onImagePlaceholderDoubleClick}
                    />
                  );
                  }

                  return null;
                })}
                {doc === "cover" && coverMode === "spread" ? (
                  <SpineTitleOverlay
                    coverMeta={docData?.cover_meta}
                    spineBox={coverSpreadGeometry?.spine || { x: pageWmm, y: 0, w: spineMm, h: pageHmm }}
                  />
                ) : null}
              </Group>

              {!isThumbnailOnly && bleedPx > 0 ? (
                <Group listening={false}>
                  {/* English comment: Shade only the bleed margin so users can distinguish trim from overrun. */}
                  <Rect
                    x={-bleedPx}
                    y={-bleedPx}
                    width={artWpx + 2 * bleedPx}
                    height={bleedPx}
                    fill="#c6bfb473"
                    listening={false}
                  />
                  <Rect
                    x={-bleedPx}
                    y={artHpx}
                    width={artWpx + 2 * bleedPx}
                    height={bleedPx}
                    fill="#c6bfb473"
                    listening={false}
                  />
                  <Rect
                    x={-bleedPx}
                    y={0}
                    width={bleedPx}
                    height={artHpx}
                    fill="#c6bfb473"
                    listening={false}
                  />
                  <Rect
                    x={artWpx}
                    y={0}
                    width={bleedPx}
                    height={artHpx}
                    fill="#c6bfb473"
                    listening={false}
                  />
                  {/* English comment: Bleed guide marks the outer edge of the printable overrun area. */}
                  <Line
                    points={[
                      -bleedPx + bleedGuideInsetPx,
                      -bleedPx + bleedGuideInsetPx,
                      artWpx + bleedPx - bleedGuideInsetPx,
                      -bleedPx + bleedGuideInsetPx,
                      artWpx + bleedPx - bleedGuideInsetPx,
                      artHpx + bleedPx - bleedGuideInsetPx,
                      -bleedPx + bleedGuideInsetPx,
                      artHpx + bleedPx - bleedGuideInsetPx,
                      -bleedPx + bleedGuideInsetPx,
                      -bleedPx + bleedGuideInsetPx,
                    ]}
                    closed={false}
                    stroke="#f97316"
                    strokeWidth={1}
                    strokeScaleEnabled={false}
                    opacity={0.8}
                    dash={[8, 6]}
                    listening={false}
                  />
                </Group>
              ) : null}

              {doc === "cover" && coverMode === "spread" ? (
                <>
                  {/* Back | Spine | Front guides */}
                  {coverSpreadGeometry?.type === "casewrap" ? (
                    <>
                      <Rect
                        x={mmToPx(coverSpreadGeometry.wrapMm)}
                        y={mmToPx(coverSpreadGeometry.wrapMm)}
                        width={mmToPx(artWmm - coverSpreadGeometry.wrapMm * 2)}
                        height={mmToPx(artHmm - coverSpreadGeometry.wrapMm * 2)}
                        stroke="#cbd5e1"
                        strokeWidth={1}
                        strokeScaleEnabled={false}
                        listening={false}
                        dash={[8, 6]}
                      />
                      <Rect
                        x={mmToPx(coverSpreadGeometry.contentBox.x)}
                        y={mmToPx(coverSpreadGeometry.contentBox.y)}
                        width={mmToPx(coverSpreadGeometry.contentBox.w)}
                        height={mmToPx(coverSpreadGeometry.contentBox.h)}
                        stroke="#22c55e"
                        strokeWidth={1}
                        strokeScaleEnabled={false}
                        opacity={0.9}
                        listening={false}
                        dash={[4, 5]}
                      />
                    </>
                  ) : null}
                  <Rect
                    x={mmToPx(coverSpreadGeometry?.spine.x ?? pageWmm)}
                    y={mmToPx(coverSpreadGeometry?.spine.y ?? 0)}
                    width={mmToPx(coverSpreadGeometry?.spine.w ?? spineMm)}
                    height={mmToPx(coverSpreadGeometry?.spine.h ?? pageHmm)}
                    stroke="#94a3b8"
                    strokeWidth={1}
                    strokeScaleEnabled={false}
                    listening={false}
                    dash={[6, 6]}
                  />
                  <Rect
                    x={mmToPx(coverSpreadGeometry?.spine.x ?? pageWmm)}
                    y={mmToPx(coverSpreadGeometry?.spine.y ?? 0)}
                    width={1}
                    height={mmToPx(coverSpreadGeometry?.spine.h ?? pageHmm)}
                    fill="#94a3b8"
                    opacity={0.7}
                    listening={false}
                  />
                  <Rect
                    x={mmToPx((coverSpreadGeometry?.spine.x ?? pageWmm) + (coverSpreadGeometry?.spine.w ?? spineMm))}
                    y={mmToPx(coverSpreadGeometry?.spine.y ?? 0)}
                    width={1}
                    height={mmToPx(coverSpreadGeometry?.spine.h ?? pageHmm)}
                    fill="#94a3b8"
                    opacity={0.7}
                    listening={false}
                  />
                  <Text
                    x={mmToPx(coverSpreadGeometry?.back.x ?? 0) + 10}
                    y={mmToPx(coverSpreadGeometry?.back.y ?? 0) + 10}
                    text="BACK"
                    fontSize={12}
                    fill="#64748b"
                    listening={false}
                  />
                  <Text
                    x={mmToPx(coverSpreadGeometry?.spine.x ?? pageWmm) + 10}
                    y={mmToPx(coverSpreadGeometry?.spine.y ?? 0) + 10}
                    text="SPINE"
                    fontSize={12}
                    fill="#64748b"
                    listening={false}
                  />
                  <Text
                    x={mmToPx(coverSpreadGeometry?.front.x ?? pageWmm + spineMm) + 10}
                    y={mmToPx(coverSpreadGeometry?.front.y ?? 0) + 10}
                    text="FRONT"
                    fontSize={12}
                    fill="#64748b"
                    listening={false}
                  />
                </>
              ) : null}

              {showContentGutter ? (
                <>
                  {showGutterShadow ? (
                    <>
                      <Rect
                        x={gutterXPx - 26}
                        y={0}
                        width={26}
                        height={artHpx}
                        listening={false}
                        fillLinearGradientStartPoint={{ x: 26, y: 0 }}
                        fillLinearGradientEndPoint={{ x: 0, y: 0 }}
                        fillLinearGradientColorStops={[
                          0, "rgba(0,0,0,0.18)",
                          1, "rgba(0,0,0,0.00)"
                        ]}
                      />
                      <Rect
                        x={gutterXPx}
                        y={0}
                        width={26}
                        height={artHpx}
                        listening={false}
                        fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                        fillLinearGradientEndPoint={{ x: 26, y: 0 }}
                        fillLinearGradientColorStops={[
                          0, "rgba(0,0,0,0.12)",
                          1, "rgba(0,0,0,0.00)"
                        ]}
                      />
                    </>
                  ) : null}
                  {/* Gutter line */}
                  <Rect
                    x={gutterXPx}
                    y={0}
                    width={1}
                    height={artHpx}
                    fill="#64748b"
                    opacity={0.45}
                    listening={false}
                  />
                  <Rect
                    x={gutterXPx + 1}
                    y={0}
                    width={1}
                    height={artHpx}
                    fill="#ffffff"
                    opacity={0.08}
                    listening={false}
                  />
                </>
              ) : null}

              {doc === "content" ? (
                <Group listening={false}>
                  {marginBoxes.map((box, index) => (
                    <Rect
                      key={`margin-box-${index}`}
                      x={mmToPx(box.x)}
                      y={mmToPx(box.y)}
                      width={mmToPx(box.w)}
                      height={mmToPx(box.h)}
                      stroke="#64748b"
                      opacity={0.45}
                      dash={[6, 6]}
                      strokeWidth={1}
                      strokeScaleEnabled={false}
                      listening={false}
                    />
                  ))}
                </Group>
              ) : null}

              {snapGuide?.xPx != null ? (
                <Rect
                  x={snapGuide.xPx}
                  y={-bleedPx}
                  width={1}
                  height={artHpx + 2 * bleedPx}
                  fill="#3b82f6"
                  listening={false}
                />
              ) : null}
              {snapGuide?.yPx != null ? (
                <Rect
                  x={-bleedPx}
                  y={snapGuide.yPx}
                  width={artWpx + 2 * bleedPx}
                  height={1}
                  fill="#3b82f6"
                  listening={false}
                />
              ) : null}
              <Transformer
                ref={trRef}
                rotateEnabled={false}
                rotateAnchorOffset={28}
                padding={0}
                ignoreStroke={true}
                anchorSize={36}
                anchorCornerRadius={14}
                anchorFill="transparent"
                anchorStroke="transparent"
                anchorStrokeWidth={0}
                borderStroke="#111827"
                borderStrokeWidth={1}
                enabledAnchors={[
                  "top-left",
                  "top-right",
                  "bottom-left",
                  "bottom-right",
                  "middle-left",
                  "middle-right",
                  "top-center",
                  "bottom-center",
                ]}
              />
              {showResizeArrows ? (() => {
                const anchors = resizeAnchorTracking && resizeAnchorPositions.length > 0
                  ? resizeAnchorPositions
                  : (getAnchorPositions() || []);
                if (anchors.length === 0) return null;

                return (
                  <Group listening={false}>
                    {anchors.map((anchor) => (
                      <AnchorArrowBadge key={anchor.name} x={anchor.x} y={anchor.y} txt={ANCHOR_ICON[anchor.name]} />
                    ))}
                  </Group>
                );
              })() : null}

              {readOnly && !isThumbnailOnly ? (
                <Rect
                  x={-bleedPx}
                  y={-bleedPx}
                  width={artWpx + 2 * bleedPx}
                  height={artHpx + 2 * bleedPx}
                  fill="rgba(255,255,255,0.001)"
                  onMouseDown={(e) => {
                    e.cancelBubble = true;
                    onFocusPage?.(pageIndex);
                    store.selectElement(doc, null);
                  }}
                  onTap={(e) => {
                    e.cancelBubble = true;
                    onFocusPage?.(pageIndex);
                    store.selectElement(doc, null);
                  }}
                />
              ) : null}
            </Layer>
          </Stage>

          {showCasewrapGuide ? (
            <div
              className="pointer-events-auto absolute left-3 top-3 z-30 max-w-[330px] rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2 text-left text-[11px] leading-4 text-slate-700 shadow-sm backdrop-blur"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-slate-900">Hardcover / cover wrap</div>
                <button
                  type="button"
                  className="-mr-1 -mt-1 rounded-md px-1.5 py-0.5 text-sm leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                  onClick={() => setCasewrapGuideClosed(true)}
                  aria-label="Zamknij legendę"
                  title="Zamknij legendę"
                >
                  ×
                </button>
              </div>
              <div className="mt-1">
                Zewnętrzna część okładki zawija się do środka. Tło i zdjęcia powinny dochodzić do
                pomarańczowej linii, a ważny tekst, logo i twarze trzymaj w panelach okładki.
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1">
                  <span className="h-0 w-5 border-t border-dashed border-slate-300" />
                  zawijka
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-0 w-5 border-t border-dashed border-green-500" />
                  panel bezpieczny
                </span>
              </div>
            </div>
          ) : null}

          {editing && !readOnly ? (
            <textarea
              autoFocus
              value={editing.value}
              onChange={(e) => setEditing((p) => (p ? { ...p, value: e.target.value } : p))}
              onBlur={() => {
                store.updateElement(editing.doc, editing.elementId, { text: editing.value });
                setEditing(null);
              }}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  e.preventDefault();
                  store.updateElement(editing.doc, editing.elementId, { text: editing.value });
                  setEditing(null);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditing(null);
                }
              }}
              style={(() => {
                const scale = editing.scale || 1;
                const grabPadPx = Math.max(0, Math.min(
                  mmToPx(2) * scale,
                  Math.max(0, (Math.min(editing.width, editing.height) - 24) / 2)
                ));
                const padPx = mmToPx(Number(editing.style?.padding_mm ?? 0)) * scale;
                return {
                position: "fixed",
                left: editing.left + grabPadPx,
                top: editing.top + grabPadPx,
                width: Math.max(24, editing.width - grabPadPx * 2),
                height: Math.max(18, editing.height - grabPadPx * 2),
                boxSizing: "border-box",
                resize: "none",
                border: "2px solid #111827",
                outline: "none",
                padding: `${padPx}px`,
                margin: "0px",
                background: "rgba(255,255,255,0.98)",
                zIndex: 50,
                color: editing.style?.color_hex || "#000000",
                fontFamily: editing.style?.font_family || "Raleway",
                fontSize: `${ptToPx(Number(editing.style?.font_size_pt ?? 18)) * editing.scale}px`,
                lineHeight: String(editing.style?.line_height || 1.2),
                fontWeight: String(editing.style?.font_weight || 400),
                fontStyle: editing.style?.italic ? "italic" : "normal",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
                };
              })()}
            />
          ) : null}

          {textToolbar && !editing && !readOnly ? (
            <div
              style={{
                position: "fixed",
                left: textToolbar.left,
                top: textToolbar.top,
                zIndex: 60
              }}
              className={TOOLBAR_WRAP}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={TOOLBAR_BTN}
                onClick={onCopySelected}
              >
                Copy
              </button>
              <button
                type="button"
                className={TOOLBAR_BTN}
                onClick={onPaste}
              >
                Paste
              </button>
              <button
                type="button"
                className={TOOLBAR_BTN}
                onClick={onDuplicate}
              >
                Duplicate
              </button>
            </div>
          ) : null}

          {imageToolbar && selectedImage && !readOnly ? (
            <div
              style={{
                position: "fixed",
                left: imageToolbar.left,
                top: imageToolbar.top,
                zIndex: 60
              }}
              className={TOOLBAR_WRAP}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {!imageCropMode ? (
                <>
                  <button
                    type="button"
                    className={TOOLBAR_BTN_ICON}
                    onClick={handleImageZoomOut}
                    title="Zoom out"
                    aria-label="Zoom out"
                  >
                    <span className="font-mono">-</span>
                    <span>Zoom</span>
                  </button>
                  <button
                    type="button"
                    className={TOOLBAR_BTN_ICON}
                    onClick={handleImageZoomIn}
                    title="Zoom in"
                    aria-label="Zoom in"
                  >
                    <span className="font-mono">+</span>
                    <span>Zoom</span>
                  </button>
                  <button
                    type="button"
                    className={TOOLBAR_BTN_ICON}
                    onClick={handleImageCrop}
                    title="Kadrowanie"
                    aria-label="Kadrowanie"
                  >
                    <span>{IMG_ICONS.crop}</span>
                    <span>Kadrowanie</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={`${TOOLBAR_BTN_ICON} ${TOOLBAR_BTN_ACTIVE}`}
                    onClick={doneCrop}
                    title="Done"
                    aria-label="Done"
                  >
                    <span>{IMG_ICONS.done}</span>
                    <span>Done</span>
                  </button>
                  <button
                    type="button"
                    className={TOOLBAR_BTN_ICON}
                    onClick={cancelCrop}
                    title="Cancel"
                    aria-label="Cancel"
                  >
                    <span>{IMG_ICONS.cancel}</span>
                    <span>Cancel</span>
                  </button>
                </>
              )}
              <button
                type="button"
                className={`${TOOLBAR_BTN_ICON} text-red-600 hover:bg-red-50`}
                onClick={handleImageRemove}
                title="Remove"
                aria-label="Remove"
              >
                <span>{IMG_ICONS.remove}</span>
                <span>Remove</span>
              </button>
            </div>
          ) : null}
          {cropModalTarget && cropModalElement ? (() => {
            const imageUrl =
              String(cropModalElement.image_url || "") ||
              (cropModalElement.source === "clipart" && cropModalElement.clipart_id
                ? getClipartPublicUrl(String(cropModalElement.clipart_id))
                : "");
            if (!imageUrl) return null;

            return (
              <ImageCropModal
                imageUrl={imageUrl}
                frameWmm={Math.max(1, Number(cropModalElement.w_mm || 1))}
                frameHmm={Math.max(1, Number(cropModalElement.h_mm || 1))}
                initialScale={Number(cropModalElement.scale ?? 1)}
                initialPanMm={{
                  x: Number(cropModalElement.pan_mm?.x ?? 0),
                  y: Number(cropModalElement.pan_mm?.y ?? 0),
                }}
                labels={{
                  title: "Kadrowanie zdjęcia",
                  apply: "Zastosuj",
                  cancel: "Anuluj",
                  hint: "Przeciągnij obszar kadrowania albo złap punkty na rogach i bokach, aby zmienić kadr.",
                }}
                onClose={() => setCropModalTarget(null)}
                onApply={(patch) => {
                  store.updateElement(
                    cropModalTarget.doc,
                    cropModalTarget.elementId,
                    buildImageCropElementPatch(cropModalElement, patch)
                  );
                  setCropModalTarget(null);
                }}
              />
            );
          })() : null}
        </div>
      </div>
    </div>
  );
}
